import React, { useState, useEffect, useMemo } from 'react';
import { AuthProvider, useAuth } from './AuthContext';
import { useTheme, ThemeProvider } from './ThemeContext';
import LoginPage from './LoginPage';
import ClientCard from './ClientCard';
import ProfileSettings from './ProfileSettings';
import Breadcrumbs from './components/Breadcrumbs';
import {
  statIcons,
  Menu,
  X,
  ArrowLeftRight,
  BarChart3,
  Users,
  Briefcase,
  Calendar,
  User,
  Settings,
  BookOpen,
  Coins,
  DollarSign,
  Euro,
  CircleDollarSign,
  Search,
  Link2,
  TrendingUp,
  TrendingDown,
  Download,
  Eye,
  Lock,
  Plus,
  Pause,
  Play,
  PlayCircle,
  Trash2,
  RefreshCw,
  Rocket,
  Crown,
  Bot,
  Zap,
  Database,
  Copy,
  Check,
  Smartphone,
  Home,
} from './components/LucideIcons';
import { getCurrencyTextClass, getCurrencyBadgeClass as getCurrencyBadgeFromUtil, getCurrencyCardClasses } from './utils/currencyColors';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';
import './App.css';

const AppContent = () => {
  const { user, loading, logout, hasPermission, isAuthenticated } = useAuth();
  const { 
    isDarkMode, 
    toggleTheme, 
    loading: themeLoading, 
    currentTheme, 
    changeTheme, 
    themes, 
    themeLabels, 
    isGritherTheme,
    customThemes,
    activeCustomTheme,
    refreshCustomThemes
  } = useTheme();
  const [botInfo, setBotInfo] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [statisticsHistory, setStatisticsHistory] = useState([]);
  const [clients, setClients] = useState([]);
  const [recentDeals, setRecentDeals] = useState([]);
  const [currencyRates, setCurrencyRates] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [forceRerender, setForceRerender] = useState(0);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [timePeriod, setTimePeriod] = useState(() => {
    // Загружаем сохраненный период из localStorage или используем 'week' по умолчанию
    const saved = localStorage.getItem('timePeriod');
    return saved && ['week', 'month', 'all'].includes(saved) ? saved : 'week';
  });

  // Pagination and filtering states
  const [clientsPage, setClientsPage] = useState(1);
  const [dealsPage, setDealsPage] = useState(1);
  
  const [clientsFilters, setClientsFilters] = useState({ showFilters: false, name: '', id: '' });
  const [dealsFilters, setDealsFilters] = useState({ 
    showFilters: false, 
    client_id: '', 
    amount_min: '', 
    amount_max: '', 
    date_from: '', 
    date_to: '' 
  });

  const [clientsSortBy, setClientsSortBy] = useState('total_thb_amount');
  const [clientsSortOrder, setClientsSortOrder] = useState('desc');
  const [dealsSortBy, setDealsSortBy] = useState('date');
  const [dealsSortOrder, setDealsSortOrder] = useState('desc');

  // Bot info will be fetched from backend
  const [botToken, setBotToken] = useState('');
  const [botUsername, setBotUsername] = useState('crm_grither_bot');

  // Очистка БД (только для админа)
  const [showClearDbModal, setShowClearDbModal] = useState(false);
  const [clearDbMode, setClearDbMode] = useState('business_only');
  const [clearDbConfirm, setClearDbConfirm] = useState('');
  const [clearDbLoading, setClearDbLoading] = useState(false);
  const [clearDbError, setClearDbError] = useState(null);
  const [clearDbSuccess, setClearDbSuccess] = useState(null);

  // Логи входов пользователей (только для админа)
  const [loginLogs, setLoginLogs] = useState([]);
  const [loginLogsLoading, setLoginLogsLoading] = useState(false);
  const [showLoginLogs, setShowLoginLogs] = useState(false);

  // Версия релиза — отображается внизу экрана для контроля обновлений
  const [releaseVersion, setReleaseVersion] = useState(null);

  // Сохраняем период времени при изменении
  useEffect(() => {
    localStorage.setItem('timePeriod', timePeriod);
  }, [timePeriod]);

  
  // Добавляем обработчик для принудительного обновления селектора тем
  useEffect(() => {
    const handleCustomThemeUpdate = (event) => {
      console.log('🔄 Received custom theme update event:', event.detail);
      // Принудительно обновляем компонент
      setForceRerender(prev => prev + 1);
      // Также обновляем кастомные темы
      if (refreshCustomThemes) {
        setTimeout(() => {
          refreshCustomThemes();
        }, 200);
      }
    };

    window.addEventListener('customThemeUpdated', handleCustomThemeUpdate);

    return () => {
      window.removeEventListener('customThemeUpdated', handleCustomThemeUpdate);
    };
  }, [refreshCustomThemes]);

  // Загрузка версии релиза с бэкенда (без авторизации)
  useEffect(() => {
    if (!isAuthenticated) return;
    const base = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${base}/api/version`)
      .then((res) => (res.ok ? res.json() : Promise.reject(res)))
      .then((data) => setReleaseVersion(data.version || null))
      .catch(() => setReleaseVersion(null));
  }, [isAuthenticated]);

  // Fetch data with authentication
  const fetchWithAuth = async (url, options = {}) => {
    const token = localStorage.getItem('token');
    
    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          ...options.headers,
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Автоматически обрабатываем 401 ошибки (неавторизованный доступ)
      if (response.status === 401) {
        localStorage.removeItem('token');
        window.location.href = '/';
        return null;
      }
      
      return response;
    } catch (error) {
      console.error('Network error in fetchWithAuth:', error);
      throw error;
    }
  };

  // Format number for display
  const formatNumber = (value) => {
    if (!value || value === '0' || value === 0) return '0';
    const num = typeof value === 'string' ? parseFloat(value.replace(/\s/g, '').replace(/,/g, '')) : value;
    return isNaN(num) ? '0' : Math.round(num).toLocaleString('ru-RU').replace(/,/g, ' ');
  };

  // Загрузка логов входов пользователей (только для админа)
  const fetchLoginLogs = async (limit = 100) => {
    if (!isAuthenticated || user?.role !== 'ADMIN') return;
    try {
      setLoginLogsLoading(true);
      const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login-logs?limit=${limit}`);
      if (response && response.ok) {
        const data = await response.json();
        setLoginLogs(data.logs || []);
      } else if (response) {
        console.error('Failed to fetch login logs:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error fetching login logs:', error);
    } finally {
      setLoginLogsLoading(false);
    }
  };

  // Fetch data functions
  const fetchStatistics = async () => {
    if (!isAuthenticated) return;
    try {
      const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/statistics`);
      if (response && response.ok) {
        const data = await response.json();
        setStatistics(data);
      } else if (response) {
        console.error('Failed to fetch statistics:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const fetchStatisticsHistory = async (period = 'week') => {
    if (!isAuthenticated) return;
    try {
      const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/statistics/history?period=${period}`);
      if (response && response.ok) {
        const data = await response.json();
        setStatisticsHistory(data.data || []);
      } else if (response) {
        console.error('Failed to fetch statistics history:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error fetching statistics history:', error);
    }
  };

  const fetchClients = async (page = 1, limit = 50, sortBy = "total_thb_amount", sortOrder = "desc", filters = {}) => {
    if (!isAuthenticated) return;
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sort_by: sortBy,
        sort_order: sortOrder
      });
      
      // Add filters if provided
      if (filters.name) params.append('filter_name', filters.name);
      if (filters.id) params.append('filter_id', filters.id);
      
      const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/clients?${params}`);
      if (response && response.ok) {
        const data = await response.json();
        setClients(Array.isArray(data) ? data : []);
      } else if (response) {
        console.error('Failed to fetch clients:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      setClients([]); // Устанавливаем пустой массив при ошибке
    }
  };

  const fetchRecentDeals = async (page = 1, limit = 50, sortBy = "date", sortOrder = "desc", filters = {}) => {
    if (!isAuthenticated) return;
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sort_by: sortBy,
        sort_order: sortOrder
      });
      
      // Add filters if provided
      if (filters.client_id) params.append('filter_client_id', filters.client_id);
      if (filters.amount_min) params.append('filter_amount_min', filters.amount_min.toString());
      if (filters.amount_max) params.append('filter_amount_max', filters.amount_max.toString());
      if (filters.date_from) params.append('filter_date_from', filters.date_from);
      if (filters.date_to) params.append('filter_date_to', filters.date_to);
      
      const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/deals/recent?${params}`);
      if (response && response.ok) {
        const data = await response.json();
        setRecentDeals(Array.isArray(data) ? data : []);
      } else if (response) {
        console.error('Failed to fetch recent deals:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error fetching recent deals:', error);
      setRecentDeals([]); // Устанавливаем пустой массив при ошибке
    }
  };


  const testBackendConnection = async () => {
    if (!isAuthenticated) return;
    try {
      setLoadingData(true);
      const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/bot-status`);
      if (response.ok) {
        const data = await response.json();
        setBotInfo(data);
      }
    } catch (error) {
      setBotInfo({ error: error.message });
    } finally {
      setLoadingData(false);
    }
  };

  /** Очистка БД (только админ). mode: business_only | full, confirm: "ОЧИСТИТЬ" */
  const clearDatabaseSubmit = async () => {
    setClearDbError(null);
    setClearDbSuccess(null);
    try {
      setClearDbLoading(true);
      const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/admin/clear-database`, {
        method: 'POST',
        body: JSON.stringify({ mode: clearDbMode, confirm: clearDbConfirm })
      });
      const data = response ? await response.json().catch(() => ({})) : {};
      if (response && response.ok) {
        setClearDbSuccess(data.message || 'Очистка выполнена.');
        setShowClearDbModal(false);
        setClearDbConfirm('');
        fetchStatistics();
        fetchRecentDeals(1, 5, 'deal_id', 'desc', {});
        if (clearDbMode === 'full') {
          setClearDbSuccess('База полностью очищена. Рекомендуется перелогиниться.');
        }
      } else {
        setClearDbError(data.detail || 'Ошибка очистки');
      }
    } catch (error) {
      setClearDbError(error.message || 'Ошибка сети');
    } finally {
      setClearDbLoading(false);
    }
  };

  const fetchCurrencyRates = async () => {
    if (!isAuthenticated) return;
    try {
      const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/currency-rates`);
      if (response.ok) {
        const data = await response.json();
        setCurrencyRates(data);
      }
    } catch (error) {
      console.error('Error fetching currency rates:', error);
    }
  };

  const exportData = async (sheetType = 'Deals') => {
    if (!hasPermission('export_data')) {
      alert('У вас нет прав для экспорта данных');
      return;
    }
    try {
      const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/export/csv?sheet_type=${sheetType}`);
      if (!response) return;
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${sheetType}_export.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        const text = await response.text();
        let msg = `Ошибка экспорта: ${response.status}`;
        try {
          const json = JSON.parse(text);
          if (json.detail) msg = typeof json.detail === 'string' ? json.detail : JSON.stringify(json.detail);
        } catch (_) {
          if (text) msg = text.slice(0, 200);
        }
        alert(msg);
      }
    } catch (error) {
      console.error('Error exporting data:', error);
      alert('Ошибка сети при экспорте. Проверьте подключение.');
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchStatistics();
      fetchStatisticsHistory(timePeriod);
      fetchClients(clientsPage, 50, clientsSortBy, clientsSortOrder, clientsFilters);
      // ✅ Для главной страницы (обзора) загружаем только 5 последних сделок
      if (activeTab === 'overview') {
        fetchRecentDeals(1, 5, 'deal_id', 'desc', {});
      } else {
        fetchRecentDeals(dealsPage, 50, dealsSortBy, dealsSortOrder, dealsFilters);
      }
      fetchCurrencyRates();
      
      // Автообновление отключено по просьбе пользователя
      // const interval = setInterval(() => {
      //   fetchStatistics();
      //   fetchRecentDeals();
      //   fetchCurrencyRates();
      // }, 30000);
      
      // return () => clearInterval(interval);
    }
  }, [isAuthenticated, activeTab]);

  // Обновление истории статистики при изменении периода
  useEffect(() => {
    if (isAuthenticated) {
      fetchStatisticsHistory(timePeriod);
    }
  }, [timePeriod]);

  // ✅ Отдельный useEffect для обновления данных при смене активной вкладки
  useEffect(() => {
    if (isAuthenticated && activeTab === 'overview') {
      // Для главной страницы загружаем только 5 последних сделок, сортированных по ID
      fetchRecentDeals(1, 5, 'deal_id', 'desc', {});
    } else if (isAuthenticated && activeTab === 'clients') {
      // При переходе на вкладку клиентов загружаем данные
      fetchClients(1, 50, clientsSortBy, clientsSortOrder, {
        name: clientsFilters.name,
        id: clientsFilters.id
      });
    } else if (isAuthenticated && activeTab === 'deals') {
      // При переходе на вкладку сделок загружаем данные
      fetchRecentDeals(1, 50, dealsSortBy, dealsSortOrder, {
        client_id: dealsFilters.client_id,
        amount_min: dealsFilters.amount_min ? parseFloat(dealsFilters.amount_min) : null,
        amount_max: dealsFilters.amount_max ? parseFloat(dealsFilters.amount_max) : null,
        date_from: dealsFilters.date_from,
        date_to: dealsFilters.date_to
      });
    }
  }, [activeTab, isAuthenticated]);

  // Helper function to get theme-specific class names
  const getThemeClass = (lightClass, darkClass, gritherClass = lightClass) => {
    if (currentTheme === 'dark') return darkClass;
    if (currentTheme === 'grither') return gritherClass;
    return lightClass;
  };

  // НОВЫЙ ПОДХОД: Полностью основанный на CSS переменных
  const getBackgroundClass = () => {
    // Всегда используем универсальный фон вместо Tailwind классов
    return 'universal-app-background';
  };

  // НОВЫЙ ПОДХОД: Полностью основанный на CSS переменных
  const getCardClass = () => {
    // Всегда используем CSS переменные вместо Tailwind классов
    return 'universal-card';
  };

  const getTextClass = (variant = 'primary') => {
    // Всегда используем CSS переменные для текста
    return variant === 'primary' ? 'universal-text-primary' : 
           variant === 'secondary' ? 'universal-text-secondary' : 
           'universal-text-tertiary';
  };

  const getButtonClass = (variant = 'primary') => {
    // Всегда используем CSS переменные для кнопок
    if (variant === 'primary') {
      return 'universal-btn-primary';
    }
    return 'universal-btn-secondary';
  };

  // Функция для получения цвета бейджа источника
  const getSourceBadgeClass = (source) => {
    if (!source) return '';
    const sourceLower = source.toLowerCase();
    if (sourceLower.includes('telegram')) {
      return currentTheme === 'dark' 
        ? 'bg-blue-900/30 text-blue-300 border border-blue-500/40' 
        : 'bg-blue-100 text-blue-800';
    }
    if (sourceLower.includes('whatsapp')) {
      return currentTheme === 'dark' 
        ? 'bg-green-900/30 text-green-300 border border-green-500/40' 
        : 'bg-green-100 text-green-800';
    }
    if (sourceLower.includes('instagram')) {
      return currentTheme === 'dark' 
        ? 'bg-pink-900/30 text-pink-300 border border-pink-500/40' 
        : 'bg-pink-100 text-pink-800';
    }
    if (sourceLower.includes('facebook')) {
      return currentTheme === 'dark' 
        ? 'bg-indigo-900/30 text-indigo-300 border border-indigo-500/40' 
        : 'bg-indigo-100 text-indigo-800';
    }
    // Дефолтный цвет
    return currentTheme === 'dark' 
      ? 'bg-gray-700 text-gray-300 border border-gray-600' 
      : 'bg-gray-100 text-gray-800';
  };

  /** Цвет бейджа валюты (единая палитра из utils/currencyColors). */
  const getCurrencyBadgeClass = (currency) => {
    if (!currency) return '';
    return getCurrencyBadgeFromUtil(currency, currentTheme === 'dark');
  };

  /** Класс текста для кода валюты (единый цвет везде). */
  const getCurrencyCodeClass = (currency) => getCurrencyTextClass(currency, currentTheme === 'dark');

  // Show loading screen while checking auth or theme — фирменная анимация бренда
  if (loading || themeLoading) {
    return (
      <div className={`min-h-screen flex flex-col items-center justify-center transition-all duration-300 ${getBackgroundClass()}`}>
        <div className="flex flex-col items-center justify-center text-center">
          <img
            src={`${process.env.PUBLIC_URL || ''}/chest_blue.svg`}
            alt=""
            className="w-24 h-24 loading-logo-spin mb-6"
            aria-hidden
          />
          <p
            className={`uppercase text-lg tracking-wide ${getTextClass('secondary')}`}
            style={{ fontFamily: "'Bounded', sans-serif" }}
          >
            GRITHER CRM LOADING...
          </p>
        </div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // Компонент выбора периода времени
  const TimePeriodSelector = () => {
    const periods = [
      { id: 'week', label: 'Неделя' },
      { id: 'month', label: 'Месяц' },
      { id: 'all', label: 'Все время' }
    ];

    return (
      <div className={`flex items-center justify-center gap-2 mb-6 ${
        currentTheme === 'dark' ? 'modern-dark-card' : 
        currentTheme === 'grither' ? 'bg-white grither-shadow grither-border' : 
        currentTheme === 'custom' ? '' : 
        'bg-white shadow-md'
      } rounded-lg p-1`}>
        {periods.map((period) => (
          <button
            key={period.id}
            onClick={() => setTimePeriod(period.id)}
            className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
              timePeriod === period.id
                ? currentTheme === 'dark'
                  ? 'bg-blue-600 text-white'
                  : currentTheme === 'grither'
                  ? 'bg-grither-primary text-white'
                  : currentTheme === 'custom'
                  ? 'bg-blue-600 text-white'
                  : 'bg-blue-600 text-white'
                : currentTheme === 'dark'
                ? 'text-slate-300 hover:text-white hover:bg-slate-700'
                : currentTheme === 'grither'
                ? 'grither-text-secondary hover:bg-grither-gray-100'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            {period.label}
          </button>
        ))}
      </div>
    );
  };

  /**
   * Возвращает понедельник недели в формате YYYY-MM-DD (локальное время) для группировки по неделям.
   * Использует только локальные методы, чтобы избежать сдвига даты в часовых поясах восточнее UTC.
   * @param {string} dateStr - Дата в формате YYYY-MM-DD
   * @returns {string|null} YYYY-MM-DD понедельника или null при невалидной дате
   */
  const getWeekStart = (dateStr) => {
    if (!dateStr || typeof dateStr !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr.trim())) {
      return null;
    }
    const d = new Date(dateStr + 'T12:00:00');
    if (Number.isNaN(d.getTime())) return null;
    const day = d.getDay();
    const diff = d.getDate() - (day === 0 ? 6 : day - 1);
    d.setDate(diff);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dayNum = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dayNum}`;
  };

  // Компонент графиков статистики
  const StatisticsCharts = () => {
    const chartData = useMemo(() => {
      if (!statisticsHistory || statisticsHistory.length === 0) return [];

      const raw = statisticsHistory.map(item => ({
        dateIso: item.date || '',
        date: item.date ? new Date(item.date).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' }) : '',
        clients: Number(item.clients) || 0,
        deals: Number(item.deals) || 0,
        turnover: Number(item.turnover) || 0
      }));

      // Убираем ведущие пустые дни до появления первых данных
      const firstNonEmpty = raw.findIndex(
        (r) => r.clients > 0 || r.deals > 0 || r.turnover > 0
      );
      const trimmed = firstNonEmpty === -1 ? raw : raw.slice(firstNonEmpty);

      if (timePeriod !== 'all') {
        return trimmed.map(({ date, clients, deals, turnover }) => ({
          date,
          clients,
          deals,
          turnover
        }));
      }

      // «Все время»: агрегация по неделям для читаемого графика
      const byWeek = {};
      trimmed.forEach((r) => {
        const key = getWeekStart(r.dateIso);
        if (key == null) return;
        if (!byWeek[key]) {
          byWeek[key] = { dateIso: key, clients: 0, deals: 0, turnover: 0 };
        }
        byWeek[key].clients += r.clients;
        byWeek[key].deals += r.deals;
        byWeek[key].turnover += r.turnover;
      });
      const weeks = Object.values(byWeek).sort(
        (a, b) => a.dateIso.localeCompare(b.dateIso)
      );
      return weeks.map((w) => ({
        date: new Date(w.dateIso + 'T12:00:00').toLocaleDateString('ru-RU', {
          day: '2-digit',
          month: '2-digit'
        }),
        clients: w.clients,
        deals: w.deals,
        turnover: w.turnover
      }));
    }, [statisticsHistory, timePeriod]);

    // Цвета для каждой темы - упрощенные и контрастные
    const getChartColors = () => {
      if (currentTheme === 'dark') {
        return {
          clients: '#60a5fa',
          deals: '#34d399',
          turnover: '#a78bfa',
          grid: '#4b5563',
          text: '#d1d5db',
          bg: '#1e293b'
        };
      } else if (currentTheme === 'grither') {
        return {
          clients: '#3b82f6',
          deals: '#10b981',
          turnover: '#8b5cf6',
          grid: '#d1d5db',
          text: '#6b7280',
          bg: '#ffffff'
        };
      } else {
        return {
          clients: '#3b82f6',
          deals: '#10b981',
          turnover: '#8b5cf6',
          grid: '#d1d5db',
          text: '#6b7280',
          bg: '#ffffff'
        };
      }
    };

    const colors = getChartColors();

    // Адаптивный интервал меток оси X: не более ~8 видимых меток
    const xAxisInterval = timePeriod === 'all'
      ? (chartData.length > 12 ? Math.max(0, Math.floor(chartData.length / 10)) : 0)
      : (chartData.length <= 10 ? 0 : Math.ceil(chartData.length / 8) - 1);

    return (
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        {/* График клиентов и сделок — по дням из БД (клиенты по first_contact_date, сделки по date) */}
        <div className={`rounded-xl shadow-lg p-6 ${
          currentTheme === 'dark' ? 'modern-dark-card' : 
          currentTheme === 'grither' ? 'bg-white grither-shadow grither-border' : 
          'bg-white'
        }`}>
          <h3 className={`text-lg font-semibold ${timePeriod === 'all' ? 'mb-1' : 'mb-4'} ${
            currentTheme === 'dark' ? 'text-white' : 
            currentTheme === 'grither' ? 'grither-text-primary' : 
            'text-gray-800'
          }`}>
            Клиенты и сделки
          </h3>
          {timePeriod === 'all' && (
            <p className={`text-xs mb-4 ${currentTheme === 'dark' ? 'text-slate-400' : 'text-gray-500'}`}>
              По неделям
            </p>
          )}
          {chartData.length === 0 ? (
            <p className={`text-sm py-8 ${currentTheme === 'dark' ? 'text-slate-400' : 'text-gray-500'}`}>
              Нет данных за выбранный период. Данные подтягиваются из добавленных клиентов и сделок — смените период или обновите страницу.
            </p>
          ) : (
          <ResponsiveContainer width="100%" height={timePeriod === 'all' ? 360 : 320}>
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} vertical={false} />
              <XAxis 
                dataKey="date" 
                stroke={colors.text}
                tick={{ fill: colors.text, fontSize: 11, angle: -40, textAnchor: 'end', dy: 4, dx: -2 }}
                height={52}
                interval={xAxisInterval}
              />
              <YAxis 
                stroke={colors.text}
                tick={{ fill: colors.text, fontSize: 11 }}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: colors.bg,
                  border: `1px solid ${colors.grid}`,
                  borderRadius: '8px',
                  fontSize: '13px'
                }}
                labelStyle={{ 
                  color: currentTheme === 'dark' ? '#ffffff' : '#000000',
                  fontWeight: 'bold',
                  marginBottom: '4px'
                }}
                itemStyle={{ 
                  color: currentTheme === 'dark' ? '#ffffff' : '#000000'
                }}
              />
              <Legend 
                wrapperStyle={{ fontSize: '13px' }}
                iconType="square"
              />
              <Bar 
                dataKey="clients" 
                name="Клиенты"
                fill={colors.clients}
                radius={[4, 4, 0, 0]}
              />
              <Bar 
                dataKey="deals" 
                name="Сделки"
                fill={colors.deals}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
          )}
        </div>

        {/* График оборота - простой Bar Chart */}
        <div className={`rounded-xl shadow-lg p-6 ${
          currentTheme === 'dark' ? 'modern-dark-card' : 
          currentTheme === 'grither' ? 'bg-white grither-shadow grither-border' : 
          'bg-white'
        }`}>
          <h3 className={`text-lg font-semibold ${timePeriod === 'all' ? 'mb-1' : 'mb-4'} ${
            currentTheme === 'dark' ? 'text-white' : 
            currentTheme === 'grither' ? 'grither-text-primary' : 
            'text-gray-800'
          }`}>
            Оборот
          </h3>
          {timePeriod === 'all' && (
            <p className={`text-xs mb-4 ${currentTheme === 'dark' ? 'text-slate-400' : 'text-gray-500'}`}>
              По неделям
            </p>
          )}
          {chartData.length === 0 ? (
            <p className={`text-sm py-8 ${currentTheme === 'dark' ? 'text-slate-400' : 'text-gray-500'}`}>
              Нет данных за выбранный период.
            </p>
          ) : (
          <ResponsiveContainer width="100%" height={timePeriod === 'all' ? 360 : 320}>
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} vertical={false} />
              <XAxis 
                dataKey="date" 
                stroke={colors.text}
                tick={{ fill: colors.text, fontSize: 11, angle: -40, textAnchor: 'end', dy: 4, dx: -2 }}
                height={52}
                interval={xAxisInterval}
              />
              <YAxis 
                stroke={colors.text}
                tick={{ fill: colors.text, fontSize: 11 }}
                tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: colors.bg,
                  border: `1px solid ${colors.grid}`,
                  borderRadius: '8px',
                  fontSize: '13px'
                }}
                labelStyle={{ 
                  color: currentTheme === 'dark' ? '#ffffff' : '#000000',
                  fontWeight: 'bold',
                  marginBottom: '4px'
                }}
                itemStyle={{ 
                  color: currentTheme === 'dark' ? '#ffffff' : '#000000'
                }}
                formatter={(value) => `${value.toLocaleString('ru-RU')} ฿`}
              />
              <Bar 
                dataKey="turnover" 
                name="Оборот (THB)"
                fill={colors.turnover}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
          )}
        </div>
      </div>
    );
  };

  const TabButton = ({ id, label, icon, active, onClick }) => (
    <button
      onClick={() => onClick(id)}
      className={`inline-flex items-center gap-2 px-4 md:px-6 py-2 md:py-3 font-medium text-xs md:text-sm rounded-lg transition-all duration-300 whitespace-nowrap ${
        active
          ? `${
              currentTheme === 'dark'
                ? 'modern-dark-tab active'
                : currentTheme === 'grither'
                ? 'grither-gradient-primary text-white grither-shadow-md hover:grither-shadow-lg'
                : currentTheme === 'custom'
                ? 'universal-tab-active'
                : 'bg-blue-600 text-white'
            } shadow-md`
          : `${
              currentTheme === 'dark'
                ? 'modern-dark-tab'
                : currentTheme === 'grither'
                ? 'bg-white grither-text-secondary hover:bg-grither-gray-100 grither-shadow hover:grither-shadow-md grither-border'
                : currentTheme === 'custom'
                ? 'universal-tab-inactive'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`
      }`}
    >
      {icon && React.isValidElement(icon) ? React.cloneElement(icon, { size: 18, strokeWidth: 2 }) : null}
      {label}
    </button>
  );

  const StatCard = ({ title, value, subtitle, icon, color = "blue" }) => {
    const getColorClass = (color) => {
      const colors = {
        blue: 'from-blue-500 to-blue-600',
        green: 'from-emerald-500 to-emerald-600',
        purple: 'from-violet-500 to-violet-600',
        orange: 'from-amber-500 to-amber-600'
      };
      return colors[color] || colors.blue;
    };

    const iconSize = 28;
    const iconEl = React.isValidElement(icon)
      ? React.cloneElement(icon, { size: iconSize, strokeWidth: 2, className: 'text-current' })
      : <span className="text-3xl md:text-4xl">{icon}</span>;

    return (
      <div className={`group relative overflow-hidden rounded-xl ${
        currentTheme === 'dark' ? 'modern-stat-card' :
        'shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5 border border-white/50'
      } ${currentTheme !== 'dark' ? getCardClass() : ''}`}>
        
        <div className="relative p-4 md:p-5">
          <div className="flex items-center gap-4">
            <div className={`flex-shrink-0 w-14 h-14 md:w-16 md:h-16 rounded-xl flex items-center justify-center ${
              currentTheme === 'dark'
                ? 'stat-icon'
                : currentTheme === 'grither'
                ? 'bg-grither-primary/20 backdrop-blur-sm border border-grither-primary/30 text-grither-primary'
                : `bg-gradient-to-br ${getColorClass(color)} text-white shadow-md`
            }`}>
              {iconEl}
            </div>
            
            {/* Текст и цифры справа */}
            <div className="flex-1 min-w-0">
              <h3 className={`text-xs md:text-sm font-medium mb-1 ${
                currentTheme === 'dark' ? 'stat-title' : getTextClass('secondary')
              }`}>{title}</h3>
              
              <p className={`text-2xl md:text-3xl lg:text-3xl font-bold mb-0.5 tabular-nums ${
                currentTheme === 'dark' ? 'stat-value text-white' :
                currentTheme === 'grither' ? 'text-grither-primary' : getTextClass('primary')
              }`}>{value}</p>
              
              {subtitle && (
                <p className={`text-xs md:text-sm font-medium ${
                  currentTheme === 'dark' ? 'stat-subtitle' : 
                  `text-${color}-600 opacity-80`
                }`}>{subtitle}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };


  const UsersManagement = () => {
    const [users, setUsers] = useState([]);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [showTelegramDebug, setShowTelegramDebug] = useState(false);
    const [createdUserResult, setCreatedUserResult] = useState(null);
    const [copyFeedback, setCopyFeedback] = useState(false);
    const [newUser, setNewUser] = useState({
      username: '',
      email: '',
      password: '',
      role: 'OPERATOR',
      telegram_id: null,
      telegram_username: ''
    });

    const fetchUsers = async () => {
      try {
        const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/auth/users`);
        if (response.ok) {
          const data = await response.json();
          setUsers(data);
        }
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };

    const deleteUser = async (userId, username) => {
      if (window.confirm(`Вы уверены, что хотите удалить пользователя "${username}"?\n\nЭто действие нельзя отменить.`)) {
        try {
          const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/auth/users/${userId}`, {
            method: 'DELETE'
          });
          
          if (response.ok) {
            const result = await response.json();
            alert(`✅ ${result.message}\n\n🔄 Кэш Telegram авторизации автоматически очищен для обеспечения мгновенного применения изменений.`);
            
            fetchUsers(); // Refresh list
          } else {
            const error = await response.json();
            alert(`❌ Ошибка удаления: ${error.detail}`);
          }
        } catch (error) {
          console.error('Error deleting user:', error);
          alert('❌ Ошибка удаления пользователя. Проверьте подключение к серверу.');
        }
      }
    };

    const toggleUserStatus = async (userId, username, currentStatus) => {
      const action = currentStatus ? 'деактивировать' : 'активировать';
      if (window.confirm(`Вы уверены, что хотите ${action} пользователя "${username}"?`)) {
        try {
          const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/auth/users/${userId}/toggle-status`, {
            method: 'PUT'
          });
          
          if (response.ok) {
            const result = await response.json();
            alert(`✅ ${result.message}\n\n🔄 Кэш Telegram авторизации автоматически обновлен для мгновенного применения изменений.`);
            
            fetchUsers(); // Refresh list
          } else {
            const error = await response.json();
            alert(`❌ Ошибка изменения статуса: ${error.detail}`);
          }
        } catch (error) {
          console.error('Error toggling user status:', error);
          alert('❌ Ошибка изменения статуса пользователя. Проверьте подключение к серверу.');
        }
      }
    };

    const clearTelegramCache = async () => {
      if (window.confirm('Очистить кэш авторизации Telegram бота?\n\nЭто поможет если пользователи не могут получить доступ к боту после изменений.')) {
        try {
          const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/auth/clear-telegram-cache`, {
            method: 'POST'
          });
          
          if (response.ok) {
            const result = await response.json();
            const clearedCount = result.cleared_users || 0;
            alert(`Кэш авторизации очищен успешно!\n\nОчищено пользователей из кэша: ${clearedCount}\n\nПользователи должны попробовать использовать бот снова.`);
          } else {
            const error = await response.json();
            alert(`Ошибка очистки кэша: ${error.detail || 'Неизвестная ошибка'}`);
          }
        } catch (error) {
          console.error('Error clearing cache:', error);
          alert('Ошибка очистки кэша авторизации. Проверьте подключение к серверу.');
        }
      }
    };

    // Новые функции для управления Telegram связями
    const linkTelegramToUser = async (userId, telegramId, telegramUsername) => {
      try {
        const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/auth/users/${userId}/telegram`, {
          method: 'PUT',
          body: JSON.stringify({
            telegram_id: parseInt(telegramId),
            telegram_username: telegramUsername
          })
        });
        
        if (response.ok) {
          const result = await response.json();
          alert(result.message);
          fetchUsers(); // Refresh list
          return true;
        } else {
          const error = await response.json();
          alert(`Ошибка связывания: ${error.detail}`);
          return false;
        }
      } catch (error) {
        console.error('Error linking telegram:', error);
        alert('Ошибка связывания с Telegram');
        return false;
      }
    };

    const unlinkTelegramFromUser = async (userId, username) => {
      if (window.confirm(`Отвязать Telegram аккаунт от пользователя "${username}"?`)) {
        try {
          const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/auth/users/${userId}/telegram`, {
            method: 'DELETE'
          });
          
          if (response.ok) {
            const result = await response.json();
            alert(`✅ ${result.message}\n\n🔄 Кэш Telegram авторизации автоматически очищен для мгновенного применения изменений.`);
            
            fetchUsers(); // Refresh list
          } else {
            const error = await response.json();
            alert(`❌ Ошибка отвязки: ${error.detail}`);
          }
        } catch (error) {
          console.error('Error unlinking telegram:', error);
          alert('❌ Ошибка отвязки Telegram. Проверьте подключение к серверу.');
        }
      }
    };

    const findUserByTelegram = async (telegramId, telegramUsername) => {
      try {
        const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/auth/telegram/find-user`, {
          method: 'POST',
          body: JSON.stringify({
            telegram_id: telegramId ? parseInt(telegramId) : null,
            telegram_username: telegramUsername
          })
        });
        
        if (response.ok) {
          const result = await response.json();
          if (result.found) {
            alert(`✅ Пользователь найден!\n\nИмя: ${result.user.username}\nEmail: ${result.user.email || 'Не указан'}\nРоль: ${result.user.role}\nTelegram ID: ${result.user.telegram_id || 'Не связан'}\nTelegram Username: ${result.user.telegram_username || 'Не указан'}\nСтатус: ${result.user.is_active ? 'Активен' : 'Неактивен'}`);
            return result.user;
          } else {
            alert('❌ Пользователь не найден');
            return null;
          }
        } else {
          const error = await response.json();
          alert(`Ошибка поиска: ${error.detail}`);
          return null;
        }
      } catch (error) {
        console.error('Error finding user:', error);
        alert('Ошибка поиска пользователя');
        return null;
      }
    };

    const testTelegramAccess = async (telegramId) => {
      try {
        const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/auth/test-telegram-access/${telegramId}`);
        
        if (response.ok) {
          const result = await response.json();
          alert(`Результат теста доступа для Telegram ID ${telegramId}:\n\n${result.message}\nСтатус: ${result.authorized ? 'Авторизован' : 'Не авторизован'}\nРоль: ${result.role || 'Нет'}\nАктивен: ${result.active ? 'Да' : 'Нет'}`);
        } else {
          alert('Ошибка тестирования доступа');
        }
      } catch (error) {
        console.error('Error testing telegram access:', error);
        alert('Ошибка тестирования доступа');
      }
    };

    const getCredentialsCopyText = (result) => {
      const u = result?.user;
      if (!u) return '';
      const lines = [
        'Логин: ' + u.username,
        result.generated_password ? 'Пароль: ' + result.generated_password : null,
        u.telegram_id ? 'Telegram ID: ' + u.telegram_id : null,
        u.telegram_username ? 'Telegram: @' + u.telegram_username : null
      ].filter(Boolean);
      return lines.join('\n');
    };

    const copyCreatedUserCredentials = async () => {
      if (!createdUserResult) return;
      const text = getCredentialsCopyText(createdUserResult);
      try {
        await navigator.clipboard.writeText(text);
        setCopyFeedback(true);
        setTimeout(() => setCopyFeedback(false), 2000);
      } catch (err) {
        console.error('Copy failed:', err);
        alert('Не удалось скопировать');
      }
    };

    const closeCreatedUserModal = async () => {
      const hadTelegramId = createdUserResult?.user?.telegram_id;
      setCreatedUserResult(null);
      setNewUser({
        username: '',
        email: '',
        password: '',
        role: 'OPERATOR',
        telegram_id: null,
        telegram_username: ''
      });
      await fetchUsers();
      if (hadTelegramId && window.confirm('Очистить кэш Telegram авторизации для применения изменений?\n\nЭто поможет новому пользователю получить доступ к боту.')) {
        await clearTelegramCache();
      }
    };

    const createUser = async (e) => {
      e.preventDefault();
      if (!newUser.telegram_id) {
        alert('Telegram ID — необходимое условие создания пользователя.');
        return;
      }
      try {
        const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/auth/users`, {
          method: 'POST',
          body: JSON.stringify(newUser)
        });
        
        if (response.ok) {
          const result = await response.json();
          setShowCreateForm(false);
          setCreatedUserResult(result);
        } else {
          const error = await response.json();
          alert(`Ошибка создания пользователя: ${error.detail}`);
        }
      } catch (error) {
        console.error('Error creating user:', error);
        alert('Ошибка создания пользователя');
      }
    };

    React.useEffect(() => {
      fetchUsers();
    }, []);

    return (
      <>
        {createdUserResult && (() => {
          const u = createdUserResult.user;
          const pwd = createdUserResult.generated_password;
          return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={closeCreatedUserModal}>
              <div
                className={`max-w-md w-full rounded-xl shadow-2xl p-6 ${
                  currentTheme === 'dark' ? 'bg-zinc-800 border border-zinc-600' : 'bg-white border border-gray-200'
                }`}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl">✅</span>
                  <h3 className={`text-xl font-semibold ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>
                    Пользователь создан успешно!
                  </h3>
                </div>
                <p className={`text-sm mb-3 ${currentTheme === 'dark' ? 'text-slate-300' : getTextClass('secondary')}`}>
                  Имя: {u.username} · Роль: {u.role} · Статус: {u.is_active ? 'Активен' : 'Неактивен'}
                </p>
                <div className={`rounded-lg p-4 mb-4 relative ${
                  currentTheme === 'dark' ? 'bg-emerald-900/30 border border-emerald-600/50' : 'bg-emerald-50 border border-emerald-200'
                }`}>
                  <button
                    type="button"
                    onClick={copyCreatedUserCredentials}
                    className={`absolute top-2 right-2 p-2 rounded-md text-sm transition-all hover:scale-110 ${
                      currentTheme === 'dark' ? 'bg-slate-700 text-white hover:bg-slate-600' : 'bg-white text-slate-600 hover:bg-slate-100'
                    }`}
                    title="Копировать данные"
                  >
                    {copyFeedback ? <Check size={18} strokeWidth={2} /> : <Copy size={18} strokeWidth={2} />}
                  </button>
                  <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${
                    currentTheme === 'dark' ? 'text-emerald-400' : 'text-emerald-700'
                  }`}>Данные для входа</p>
                  <div className={`space-y-1.5 font-mono text-sm ${currentTheme === 'dark' ? 'text-slate-200' : 'text-gray-800'}`}>
                    <div><span className="text-slate-500">Логин:</span> {u.username}</div>
                    {pwd && <div><span className="text-slate-500">Пароль:</span> {pwd}</div>}
                    {u.telegram_id && <div><span className="text-slate-500">Telegram ID:</span> {u.telegram_id}</div>}
                    {u.telegram_username && <div><span className="text-slate-500">Telegram:</span> @{u.telegram_username}</div>}
                  </div>
                  {pwd && (
                    <p className={`text-xs mt-2 ${currentTheme === 'dark' ? 'text-amber-400' : 'text-amber-700'}`}>
                      ⚠ Сохраните пароль — он больше не будет показан.
                    </p>
                  )}
                </div>
                {u.telegram_id && (
                  <p className={`text-sm mb-4 ${currentTheme === 'dark' ? 'text-slate-400' : getTextClass('secondary')}`}>
                    📱 Пользователь должен написать боту сообщение /start для начала работы.
                  </p>
                )}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={closeCreatedUserModal}
                    className={`w-full px-4 py-2 rounded-lg text-sm font-medium ${getButtonClass('primary')}`}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
      <div className={`rounded-xl shadow-lg p-6 transition-all duration-300 ${currentTheme === 'dark' ? 'modern-dark-card' : getCardClass()}`}>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3">
          <h2 className={`text-xl sm:text-2xl font-bold ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>
            Управление пользователями
          </h2>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={clearTelegramCache}
              className={`inline-flex items-center gap-2 px-2 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm rounded-lg transition-all duration-300 hover:scale-105 ${currentTheme === 'dark' ? 'bg-slate-600 text-white hover:bg-slate-500 border border-slate-500' : getButtonClass('secondary')}`}
              title="Очистить кэш Telegram авторизации"
            >
              <RefreshCw size={18} strokeWidth={2} /> <span className="hidden sm:inline">Очистить кэш</span><span className="sm:hidden">Кэш</span>
            </button>
            <button
              onClick={() => setShowTelegramDebug(!showTelegramDebug)}
              className={`inline-flex items-center gap-2 px-2 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm rounded-lg transition-all duration-300 hover:scale-105 ${currentTheme === 'dark' ? 'bg-slate-600 text-white hover:bg-slate-500 border border-slate-500' : getButtonClass('secondary')}`}
            >
              <Search size={18} strokeWidth={2} /> <span className="hidden sm:inline">Отладка Telegram</span><span className="sm:hidden">Отладка</span>
            </button>
            <button
              onClick={() => setShowCreateForm(!showCreateForm)}
              className={`inline-flex items-center gap-2 px-2 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm rounded-lg transition-all duration-300 hover:scale-105 ${getButtonClass('primary')}`}
            >
              {showCreateForm ? <X size={18} strokeWidth={2} /> : <Plus size={18} strokeWidth={2} />} {showCreateForm ? 'Отменить' : 'Добавить'}
            </button>
          </div>
        </div>

        {showCreateForm && (
          <div className={`mb-6 p-4 rounded-lg transition-all duration-300 ${
            currentTheme === 'dark' ? 'bg-zinc-800/50 border border-zinc-700' :
            currentTheme === 'grither' ? 'bg-grither-gray-50' : 'bg-gray-50'
          }`}>
            <h3 className={`text-lg font-semibold mb-4 ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>
              Создать нового пользователя
            </h3>
            <form onSubmit={createUser} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-1 ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>
                  Имя пользователя
                </label>
                <input
                  type="text"
                  value={newUser.username}
                  onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300 ${
                    currentTheme === 'dark' 
                      ? 'modern-dark-input' 
                      : currentTheme === 'grither'
                      ? 'bg-white border-grither-gray-300 text-grither-text-primary' 
                      : currentTheme === 'custom'
                      ? 'universal-input'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  required
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>
                  Email (опционально)
                </label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300 ${
                    currentTheme === 'dark' 
                      ? 'modern-dark-input' 
                      : currentTheme === 'grither'
                      ? 'bg-white border-grither-gray-300 text-grither-text-primary' 
                      : currentTheme === 'custom'
                      ? 'universal-input'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>
                  Telegram ID
                </label>
                <input
                  type="number"
                  value={newUser.telegram_id || ''}
                  onChange={(e) => setNewUser({...newUser, telegram_id: e.target.value ? parseInt(e.target.value) : null})}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300 ${
                    currentTheme === 'dark' 
                      ? 'modern-dark-input' 
                      : currentTheme === 'grither'
                      ? 'bg-white border-grither-gray-300 text-grither-text-primary' 
                      : currentTheme === 'custom'
                      ? 'universal-input'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  placeholder="123456789"
                  required
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>
                  Telegram Username (опционально)
                </label>
                <input
                  type="text"
                  value={newUser.telegram_username || ''}
                  onChange={(e) => setNewUser({...newUser, telegram_username: e.target.value})}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300 ${
                    currentTheme === 'dark' 
                      ? 'modern-dark-input' 
                      : currentTheme === 'grither'
                      ? 'bg-white border-grither-gray-300 text-grither-text-primary' 
                      : currentTheme === 'custom'
                      ? 'universal-input'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  placeholder="username"
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>
                  Пароль (оставьте пустым для автогенерации)
                </label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300 ${
                    currentTheme === 'dark' 
                      ? 'modern-dark-input' 
                      : currentTheme === 'grither'
                      ? 'bg-white border-grither-gray-300 text-grither-text-primary' 
                      : currentTheme === 'custom'
                      ? 'universal-input'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>
                  Роль
                </label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300 ${
                    currentTheme === 'dark' 
                      ? 'modern-dark-input' 
                      : currentTheme === 'grither'
                      ? 'bg-white border-grither-gray-300 text-grither-text-primary' 
                      : currentTheme === 'custom'
                      ? 'universal-input'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  <option value="OPERATOR">Оператор</option>
                  <option value="CASHIER">Кассир</option>
                  <option value="ADMIN">Администратор</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <button
                  type="submit"
                  className={`px-4 py-2 rounded-lg transition-all duration-300 hover:scale-105 ${getButtonClass('primary')}`}
                >
                  Создать пользователя
                </button>
              </div>
            </form>
          </div>
        )}

        {showTelegramDebug && (
          <div className={`mb-6 p-4 rounded-lg border-l-4 ${
            currentTheme === 'dark' ? 'bg-zinc-800/50 border border-zinc-700 border-l-yellow-500' :
            currentTheme === 'grither' ? 'bg-grither-gray-50 border-l-grither-primary' : 'bg-yellow-50 border-l-yellow-400'
          }`}>
            <h3 className={`text-lg font-semibold mb-3 ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>
              🔍 Отладка доступа к Telegram боту
            </h3>
            <div className={`text-sm mb-4 ${currentTheme === 'dark' ? 'text-slate-200' : getTextClass('secondary')}`}>
              <p>• Для использования Telegram бота пользователь должен быть создан с указанным Telegram ID</p>
              <p>• Пользователь должен быть активным (статус "Активен")</p>
              <p>• После изменений может потребоваться очистка кэша авторизации</p>
              <p>• Бот должен быть запущен и работать</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <h4 className={`font-medium mb-2 ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>Статус системы</h4>
                <div className={`text-sm ${currentTheme === 'dark' ? 'text-slate-200' : getTextClass('secondary')}`}>
                  <p>• Пользователей с Telegram ID: {users.filter(u => u.telegram_id).length}</p>
                  <p>• Активных пользователей: {users.filter(u => u.is_active).length}</p>
                  <p>• Администраторов: {users.filter(u => u.role === 'ADMIN').length}</p>
                </div>
              </div>
              
              <div>
                <h4 className={`font-medium mb-2 ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>Поиск пользователей</h4>
                <div className="space-y-2">
                  <button
                    onClick={() => {
                      const telegramId = prompt('Введите Telegram ID для поиска:');
                      if (telegramId) {
                        findUserByTelegram(telegramId, null);
                      }
                    }}
                    className={`w-full px-3 py-1 text-sm rounded ${currentTheme === 'dark' ? 'bg-slate-600 text-white hover:bg-slate-500 border border-slate-500' : getButtonClass('secondary')}`}
                  >
                    🔍 Найти по ID
                  </button>
                  <button
                    onClick={() => {
                      const telegramUsername = prompt('Введите Telegram username (без @):');
                      if (telegramUsername) {
                        findUserByTelegram(null, telegramUsername);
                      }
                    }}
                    className={`w-full px-3 py-1 text-sm rounded ${currentTheme === 'dark' ? 'bg-slate-600 text-white hover:bg-slate-500 border border-slate-500' : getButtonClass('secondary')}`}
                  >
                    🔍 Найти по @username
                  </button>
                </div>
              </div>
              
              <div>
                <h4 className={`font-medium mb-2 ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>Действия</h4>
                <button
                  onClick={clearTelegramCache}
                  className={`w-full mb-2 px-3 py-2 text-sm rounded-lg ${currentTheme === 'dark' ? 'bg-slate-600 text-white hover:bg-slate-500 border border-slate-500' : getButtonClass('secondary')}`}
                >
                  Очистить кэш авторизации
                </button>
                <p className={`text-xs ${getTextClass('secondary')}`}>
                  Используйте если пользователи не могут получить доступ после изменений
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Мобильный вид: карточки пользователей */}
        <div className="md:hidden space-y-3">
          {users.map((user, index) => (
            <div
              key={index}
              className={`rounded-lg border p-4 ${
                currentTheme === 'dark' ? 'bg-slate-800 border-slate-600' :
                currentTheme === 'grither' ? 'bg-white border-grither-gray-200' :
                'bg-white border-gray-200'
              }`}
            >
              <div className="flex justify-between items-start gap-2 mb-2">
                <span className={`font-medium text-sm truncate ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>{user.username}</span>
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full shrink-0 ${
                  currentTheme === 'dark'
                    ? (user.role === 'ADMIN' ? 'bg-red-500/40 text-white' : user.role === 'CASHIER' ? 'bg-yellow-500/40 text-white' : 'bg-green-500/40 text-white')
                    : (user.role === 'ADMIN' ? 'bg-red-100 text-red-800' : user.role === 'CASHIER' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800')
                }`}>
                  {user.role === 'CASHIER' ? 'Кассир' : user.role === 'OPERATOR' ? 'Оператор' : user.role === 'ADMIN' ? 'Админ' : user.role}
                </span>
              </div>
              <div className={`text-xs mb-2 ${currentTheme === 'dark' ? 'text-slate-300' : getTextClass('secondary')}`}>
                {user.email || '—'} · {user.telegram_id ? `ID: ${user.telegram_id}${user.telegram_username ? ` @${user.telegram_username}` : ''}` : 'TG не связан'}
              </div>
              <div className="flex flex-wrap gap-1 mb-2">
                <span className={`px-2 py-0.5 text-xs rounded-full ${user.is_active ? (currentTheme === 'dark' ? 'bg-green-500/40 text-white' : 'bg-green-100 text-green-800') : (currentTheme === 'dark' ? 'bg-red-500/40 text-white' : 'bg-red-100 text-red-800')}`}>
                  {user.is_active ? 'Активен' : 'Неактивен'}
                </span>
                {user.last_login && (
                  <span className={`text-xs ${currentTheme === 'dark' ? 'text-slate-400' : 'text-gray-500'}`}>
                    Вход: {new Date(user.last_login).toLocaleDateString('ru-RU')}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-1 pt-2 border-t border-slate-600/50">
                <button
                  onClick={() => toggleUserStatus(user.id, user.username, user.is_active)}
                  className={`px-2 py-1 text-xs rounded ${currentTheme === 'dark' ? (user.is_active ? 'bg-yellow-500/40 text-white' : 'bg-green-500/40 text-white') : (user.is_active ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800')}`}
                  title={user.is_active ? 'Деактивировать' : 'Активировать'}
                >
                  {user.is_active ? <Pause size={16} strokeWidth={2} /> : <Play size={16} strokeWidth={2} />}
                </button>
                {user.telegram_id ? (
                  <>
                    <button onClick={() => testTelegramAccess(user.telegram_id)} className={`p-2 text-xs rounded ${currentTheme === 'dark' ? 'bg-blue-500/40 text-white' : 'bg-blue-100 text-blue-800'}`} title="Тест TG"><Search size={16} strokeWidth={2} /></button>
                    <button onClick={() => unlinkTelegramFromUser(user.id, user.username)} className={`p-2 text-xs rounded ${currentTheme === 'dark' ? 'bg-orange-500/40 text-white' : 'bg-orange-100 text-orange-800'}`} title="Отвязать TG"><Link2 size={16} strokeWidth={2} /></button>
                  </>
                ) : (
                  <button
                    onClick={() => { const id = prompt('Telegram ID:'); if (id) { const un = prompt('@username (опц.):'); linkTelegramToUser(user.id, id, un || null); } }}
                    className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded ${currentTheme === 'dark' ? 'bg-purple-500/40 text-white' : 'bg-purple-100 text-purple-800'}`}
                    title="Связать TG"
                  >
                    <Link2 size={16} strokeWidth={2} /> TG
                  </button>
                )}
                {user.username !== 'admin' && (
                  <button onClick={() => deleteUser(user.id, user.username)} className={`p-2 text-xs rounded ${currentTheme === 'dark' ? 'bg-red-500/40 text-white' : 'bg-red-100 text-red-800'}`} title="Удалить"><Trash2 size={16} strokeWidth={2} /></button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Десктоп: таблица с адаптивными отступами и текстом */}
        <div className={`hidden md:block overflow-x-auto rounded-lg ${currentTheme === 'dark' ? 'modern-dark-table' : ''}`}>
          <table className={`w-full table-auto min-w-[640px] ${
            currentTheme === 'dark' ? 'modern-dark-table bg-slate-800 border border-slate-600' : 
            currentTheme === 'grither' ? 'custom-theme-table universal-table' :
            currentTheme === 'custom' ? 'custom-theme-table universal-table' :
            'universal-table bg-white rounded-lg overflow-hidden shadow-sm border border-gray-200'
          }`}>
                <thead>
              <tr className={`${
                currentTheme === 'dark' ? 'bg-slate-800 border-b border-slate-600' : 'universal-table-header'
              } ${
                currentTheme === 'grither' ? 'custom-theme-table-header' : 
                currentTheme === 'custom' ? 'custom-theme-table-header' :
                currentTheme !== 'dark' ? 'bg-gray-50' : ''
              }`}>
                <th className={`px-3 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm font-medium ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>
                  Пользователь
                </th>
                <th className={`px-3 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm font-medium ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>
                  Email
                </th>
                <th className={`px-3 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm font-medium ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>
                  Роль
                </th>
                <th className={`px-3 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm font-medium ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>
                  Telegram
                </th>
                <th className={`px-3 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm font-medium ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>
                  Статус
                </th>
                <th className={`px-3 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm font-medium ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>
                  Последний вход
                </th>
                <th className={`px-3 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm font-medium ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>
                  Действия
                </th>
              </tr>
            </thead>
            <tbody>
              {users.map((user, index) => (
                <tr key={index} className={`${
                  currentTheme === 'dark' ? 'bg-slate-800 border-b border-slate-600 hover:bg-slate-700' : 'universal-table-row'
                } ${
                  currentTheme === 'grither' ? 'custom-theme-table-stripe custom-theme-table-hover' : 
                  currentTheme === 'custom' ? 'custom-theme-table-stripe custom-theme-table-hover' :
                  currentTheme !== 'dark' ? 'border-gray-200 hover:bg-gray-50' : ''
                }`}>
                  <td className={`px-4 py-3 text-sm font-medium ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>
                    {user.username}
                  </td>
                  <td className={`px-4 py-3 text-sm ${currentTheme === 'dark' ? 'text-slate-200' : getTextClass('secondary')}`}>
                    {user.email || 'Не указан'}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      currentTheme === 'dark'
                        ? (user.role === 'ADMIN' 
                            ? 'bg-red-500/40 text-white' 
                            : user.role === 'CASHIER' 
                            ? 'bg-yellow-500/40 text-white' 
                            : 'bg-green-500/40 text-white')
                        : (user.role === 'ADMIN' 
                            ? 'bg-red-100 text-red-800' 
                            : user.role === 'CASHIER' 
                            ? 'bg-yellow-100 text-yellow-800' 
                            : 'bg-green-100 text-green-800')
                    }`}>
                      {user.role === 'CASHIER' ? 'Кассир' : user.role === 'OPERATOR' ? 'Оператор' : user.role === 'ADMIN' ? 'Администратор' : user.role}
                    </span>
                  </td>
                  <td className={`px-4 py-3 text-sm ${currentTheme === 'dark' ? 'text-slate-200' : getTextClass('secondary')}`}>
                    {user.telegram_id ? (
                      <div>
                        <div>ID: {user.telegram_id}</div>
                        {user.telegram_username && <div>@{user.telegram_username}</div>}
                      </div>
                    ) : 'Не связан'}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      currentTheme === 'dark'
                        ? (user.is_active ? 'bg-green-500/40 text-white' : 'bg-red-500/40 text-white')
                        : (user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800')
                    }`}>
                      {user.is_active ? 'Активен' : 'Неактивен'}
                    </span>
                  </td>
                  <td className={`px-4 py-3 text-sm ${currentTheme === 'dark' ? 'text-slate-200' : getTextClass('secondary')}`}>
                    {user.last_login ? new Date(user.last_login).toLocaleString('ru-RU') : 'Никогда'}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex flex-wrap gap-2">
                      {/* Toggle Status Button */}
                      <button
                        onClick={() => toggleUserStatus(user.id, user.username, user.is_active)}
                        className={`px-2 py-1 text-xs rounded transition-all duration-200 hover:scale-105 ${
                          currentTheme === 'dark'
                            ? (user.is_active ? 'bg-yellow-500/40 text-white hover:bg-yellow-500/50' : 'bg-green-500/40 text-white hover:bg-green-500/50')
                            : (user.is_active ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' : 'bg-green-100 text-green-800 hover:bg-green-200')
                        }`}
                        title={user.is_active ? 'Деактивировать' : 'Активировать'}
                      >
                        {user.is_active ? <><Pause size={14} strokeWidth={2} className="inline-block align-middle mr-1" /> Деактивировать</> : <><PlayCircle size={14} strokeWidth={2} className="inline-block align-middle mr-1" /> Активировать</>}
                      </button>
                      
                      {/* Telegram Management Buttons */}
                      {user.telegram_id ? (
                        <>
                          <button
                            onClick={() => testTelegramAccess(user.telegram_id)}
                            className={`px-2 py-1 text-xs rounded transition-all duration-200 hover:scale-105 ${
                              currentTheme === 'dark' ? 'bg-blue-500/40 text-white hover:bg-blue-500/50' : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                            }`}
                            title="Проверить доступ к Telegram боту"
                          >
                            <Search size={14} strokeWidth={2} className="inline-block align-middle mr-1" /> Тест Telegram
                          </button>
                          <button
                            onClick={() => unlinkTelegramFromUser(user.id, user.username)}
                            className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded transition-all duration-200 hover:scale-105 ${
                              currentTheme === 'dark' ? 'bg-orange-500/40 text-white hover:bg-orange-500/50' : 'bg-orange-100 text-orange-800 hover:bg-orange-200'
                            }`}
                            title="Отвязать Telegram аккаунт"
                          >
                            <Link2 size={14} strokeWidth={2} /> Отвязать TG
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => {
                            const telegramId = prompt('Введите Telegram ID пользователя:');
                            if (telegramId) {
                              const telegramUsername = prompt('Введите Telegram username (без @, опционально):');
                              linkTelegramToUser(user.id, telegramId, telegramUsername);
                            }
                          }}
                          className={`px-2 py-1 text-xs rounded transition-all duration-200 hover:scale-105 ${
                            currentTheme === 'dark' ? 'bg-purple-500/40 text-white hover:bg-purple-500/50' : 'bg-purple-100 text-purple-800 hover:bg-purple-200'
                          }`}
                          title="Связать с Telegram аккаунтом"
                        >
                          <Link2 size={14} strokeWidth={2} className="inline-block align-middle mr-1" /> Связать TG
                        </button>
                      )}
                      
                      {user.username !== 'admin' && (
                        <button
                          onClick={() => deleteUser(user.id, user.username)}
                          className={`px-2 py-1 text-xs rounded transition-all duration-200 hover:scale-105 ${
                            currentTheme === 'dark' ? 'bg-red-500/40 text-white hover:bg-red-500/50' : 'bg-red-100 text-red-800 hover:bg-red-200'
                          }`}
                          title="Удалить пользователя"
                        >
                          <Trash2 size={14} strokeWidth={2} className="inline-block align-middle mr-1" /> Удалить
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {users.length === 0 && (
          <div className={`text-center py-8 ${getTextClass('secondary')}`}>
            <div className="flex justify-center mb-2"><User size={48} strokeWidth={2} /></div>
            <p>Пользователей пока нет</p>
          </div>
        )}
      </div>
      </>
    );
  };

  return (
    <div className={`min-h-screen transition-all duration-300 ${getBackgroundClass()}`}>
      {/* Enhanced Header - Адаптивный для мобильных */}
      <div className={`shadow-lg transition-all duration-300 ${
        currentTheme === 'dark' 
          ? 'modern-dark-header' 
          : currentTheme === 'grither'
          ? 'grither-gradient-header border-b border-grither-gray-200 grither-shadow-lg backdrop-blur-sm'
          : 'bg-white'
      }`}>
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="flex justify-between items-center py-3 md:py-6">
            {/* Логотип и заголовок — клик ведёт на главную */}
            <button
              type="button"
              onClick={() => {
                setActiveTab('overview');
                setSelectedClientId(null);
                setIsMobileMenuOpen(false);
              }}
              className="flex items-center space-x-3 rounded-lg transition-opacity hover:opacity-90 focus:outline-none"
              aria-label="На главную"
            >
              <div className="flex-shrink-0">
                <div className={`w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center shadow-lg ${
                  currentTheme === 'dark'
                    ? 'bg-gradient-to-br from-blue-600 to-purple-600' 
                    : currentTheme === 'grither'
                    ? 'bg-white/20 backdrop-blur-sm grither-shadow-lg hover:bg-white/30'
                    : 'bg-gradient-to-br from-blue-600 to-indigo-600'
                }`}>
                  <img
                    src={`${process.env.PUBLIC_URL || ''}/white_chest.svg`}
                    alt="Логотип"
                    className="w-5 h-5 md:w-6 md:h-6 object-contain"
                  />
                </div>
              </div>
              <div className="ml-0 text-left">
                <h1 className={`text-lg md:text-2xl font-bold ${
                  currentTheme === 'grither' ? 'grither-text-white' : getTextClass('primary')
                }`}>
                  CRM Dashboard
                </h1>
                <p className={`text-xs md:text-sm hidden sm:block ${
                  currentTheme === 'grither' ? 'grither-text-white opacity-90' : getTextClass('secondary')
                }`}>
                  Система управления клиентами и сделками
                </p>
              </div>
            </button>
            
            {/* Бургер-меню кнопка (только на мобильных) - СПРАВА */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`md:hidden w-10 h-10 rounded-lg flex items-center justify-center transition-all ${
                currentTheme === 'dark'
                  ? 'bg-slate-700 hover:bg-slate-600 text-white'
                  : currentTheme === 'grither'
                  ? 'bg-white/20 hover:bg-white/30 text-white'
                  : 'bg-gray-100 hover:bg-gray-200'
              }`}
              aria-label="Меню"
            >
              {isMobileMenuOpen ? (
                <X size={24} strokeWidth={2} />
              ) : (
                <Menu size={24} strokeWidth={2} />
              )}
            </button>
            
            {/* Правая панель - адаптивная */}
            <div className="hidden md:flex items-center gap-2 md:gap-4">
              {/* Селектор темы */}
              {isAuthenticated && (
                <div className="relative">
                  <select
                    value={currentTheme === 'custom' && activeCustomTheme ? `custom-${activeCustomTheme.id}` : currentTheme}
                    onChange={(e) => {
                      const selectedValue = e.target.value;
                      if (selectedValue.startsWith('custom-')) {
                        const customThemeId = selectedValue.replace('custom-', '');
                        changeTheme('custom', customThemeId);
                      } else {
                        changeTheme(selectedValue);
                      }
                    }}
                    className={`px-2 md:px-3 py-2 rounded-lg text-xs md:text-sm font-medium transition-all duration-300 border focus:ring-2 ${
                      currentTheme === 'dark'
                        ? 'bg-gray-700 text-white border-gray-600 focus:ring-2 focus:ring-blue-500' 
                        : currentTheme === 'grither'
                        ? 'bg-white/95 text-grither-dark border-grither-gray-300 focus:ring-2 focus:ring-grither-primary/50 backdrop-blur-sm grither-shadow hover:bg-white'
                        : currentTheme === 'custom'
                        ? 'custom-theme-selector'
                        : 'bg-white text-gray-900 border-gray-300 focus:ring-2 focus:ring-blue-500'
                    }`}
                    title="Выбор темы оформления"
                  >
                    {Object.entries(themeLabels).map(([themeKey, label]) => {
                      if (themeKey === 'custom') return null;
                      return (
                        <option key={themeKey} value={themeKey}>
                          {themeKey === 'light' && '☀ '}
                          {themeKey === 'dark' && '☽ '}
                          {themeKey === 'grither' && '◇ '}
                          {label}
                        </option>
                      );
                    })}
                    {customThemes.length > 0 && (
                      <optgroup label="Кастомные темы">
                        {customThemes.map(theme => (
                          <option key={`custom-${theme.id}`} value={`custom-${theme.id}`}>
                            {theme.is_active && '● '}{theme.display_name}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                </div>
              )}
              
              {/* Статус бота */}
              <div className="flex items-center">
                <div className="w-2 h-2 md:w-3 md:h-3 bg-green-400 rounded-full animate-pulse"></div>
                <span className={`ml-1 md:ml-2 text-xs md:text-sm font-medium ${
                  currentTheme === 'grither' ? 'text-white/90' : getTextClass('secondary')
                }`}>
                  Бот активен
                </span>
              </div>
              
              {/* Информация о пользователе */}
              <div className="flex items-center space-x-3">
                <div className="text-right">
                  <p className={`text-sm ${
                    currentTheme === 'grither' ? 'text-white/80' : getTextClass('tertiary')
                  }`}>
                    Пользователь
                  </p>
                  <p className={`text-sm font-bold ${
                    currentTheme === 'dark' 
                      ? 'text-blue-400' 
                      : currentTheme === 'grither'
                      ? 'text-white'
                      : 'text-blue-600'
                  }`}>
                    {user?.username} ({user?.role})
                  </p>
                </div>
                <button
                  onClick={logout}
                  className="px-4 py-2 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all duration-300 hover:scale-105"
                >
                  Выйти
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Мобильное боковое меню - СПРАВА */}
      {isMobileMenuOpen && (
        <>
          {/* Overlay */}
          <div 
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          
          {/* Sidebar - выезжает справа; flex, чтобы версия была внизу */}
          <div className={`fixed top-0 right-0 h-full w-64 z-50 flex flex-col md:hidden ${
            currentTheme === 'dark'
              ? 'bg-slate-800'
              : currentTheme === 'grither'
              ? 'bg-gradient-to-b from-grither-primary to-grither-primary-dark'
              : 'bg-white'
          } shadow-2xl`}>
            {/* Прокручиваемая часть меню */}
            <div className="flex-1 min-h-0 overflow-y-auto">
            {/* Заголовок меню */}
            <div className={`p-4 border-b ${
              currentTheme === 'dark'
                ? 'border-slate-700'
                : currentTheme === 'grither'
                ? 'border-white/20'
                : 'border-gray-200'
            }`}>
              <div className="flex items-center justify-between">
                <h2 className={`text-lg font-bold ${
                  currentTheme === 'grither' ? 'text-white' : getTextClass('primary')
                }`}>
                  Навигация
                </h2>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`p-2 rounded-lg ${
                    currentTheme === 'dark'
                      ? 'hover:bg-slate-700'
                      : currentTheme === 'grither'
                      ? 'hover:bg-white/10'
                      : 'hover:bg-gray-100'
                  }`}
                >
                  <svg
                    className={`w-6 h-6 ${
                      currentTheme === 'grither' ? 'text-white' : getTextClass('primary')
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Информация о пользователе */}
            <div className={`p-4 border-b ${
              currentTheme === 'dark'
                ? 'border-slate-700'
                : currentTheme === 'grither'
                ? 'border-white/20'
                : 'border-gray-200'
            }`}>
              <div className="flex items-center space-x-3">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  currentTheme === 'dark'
                    ? 'bg-blue-600'
                    : currentTheme === 'grither'
                    ? 'bg-white/20'
                    : 'bg-blue-100'
                }`}>
                  <span className={`text-xl font-bold ${
                    currentTheme === 'grither' ? 'text-white' : 
                    currentTheme === 'dark' ? 'text-white' : 'text-blue-600'
                  }`}>
                    {user?.username?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className={`font-bold ${
                    currentTheme === 'grither' ? 'text-white' : getTextClass('primary')
                  }`}>
                    {user?.username}
                  </p>
                  <p className={`text-sm ${
                    currentTheme === 'grither' ? 'text-white/70' : getTextClass('secondary')
                  }`}>
                    {user?.role}
                  </p>
                </div>
              </div>
            </div>

            {/* Пункты меню */}
            <nav className="p-2">
              <button
                onClick={() => {
                  setActiveTab('overview');
                  setIsMobileMenuOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all mb-1 ${
                  activeTab === 'overview'
                    ? currentTheme === 'dark'
                      ? 'bg-blue-600 text-white'
                      : currentTheme === 'grither'
                      ? 'bg-white/20 text-white'
                      : 'bg-blue-100 text-blue-600'
                    : currentTheme === 'dark'
                    ? 'hover:bg-slate-700 text-slate-300'
                    : currentTheme === 'grither'
                    ? 'hover:bg-white/10 text-white/80'
                    : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                <BarChart3 size={20} strokeWidth={2} className="flex-shrink-0" />
                <span className="font-medium">Обзор</span>
              </button>

              <button
                onClick={() => {
                  setActiveTab('clients');
                  setIsMobileMenuOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all mb-1 ${
                  activeTab === 'clients'
                    ? currentTheme === 'dark'
                      ? 'bg-blue-600 text-white'
                      : currentTheme === 'grither'
                      ? 'bg-white/20 text-white'
                      : 'bg-blue-100 text-blue-600'
                    : currentTheme === 'dark'
                    ? 'hover:bg-slate-700 text-slate-300'
                    : currentTheme === 'grither'
                    ? 'hover:bg-white/10 text-white/80'
                    : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                <Users size={20} strokeWidth={2} className="flex-shrink-0" />
                <span className="font-medium">Клиенты</span>
              </button>

              <button
                onClick={() => {
                  setActiveTab('deals');
                  setIsMobileMenuOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all mb-1 ${
                  activeTab === 'deals'
                    ? currentTheme === 'dark'
                      ? 'bg-blue-600 text-white'
                      : currentTheme === 'grither'
                      ? 'bg-white/20 text-white'
                      : 'bg-blue-100 text-blue-600'
                    : currentTheme === 'dark'
                    ? 'hover:bg-slate-700 text-slate-300'
                    : currentTheme === 'grither'
                    ? 'hover:bg-white/10 text-white/80'
                    : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                <Briefcase size={20} strokeWidth={2} className="flex-shrink-0" />
                <span className="font-medium">Сделки</span>
              </button>

              <button
                onClick={() => {
                  setActiveTab('rates');
                  setIsMobileMenuOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all mb-1 ${
                  activeTab === 'rates'
                    ? currentTheme === 'dark'
                      ? 'bg-blue-600 text-white'
                      : currentTheme === 'grither'
                      ? 'bg-white/20 text-white'
                      : 'bg-blue-100 text-blue-600'
                    : currentTheme === 'dark'
                    ? 'hover:bg-slate-700 text-slate-300'
                    : currentTheme === 'grither'
                    ? 'hover:bg-white/10 text-white/80'
                    : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                <ArrowLeftRight size={20} strokeWidth={2} className="flex-shrink-0" />
                <span className="font-medium">Курсы валют</span>
              </button>

              {hasPermission('manage_users') && (
                <button
                  onClick={() => {
                    setActiveTab('users');
                    setIsMobileMenuOpen(false);
                  }}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all mb-1 ${
                    activeTab === 'users'
                      ? currentTheme === 'dark'
                        ? 'bg-blue-600 text-white'
                        : currentTheme === 'grither'
                        ? 'bg-white/20 text-white'
                        : 'bg-blue-100 text-blue-600'
                      : currentTheme === 'dark'
                      ? 'hover:bg-slate-700 text-slate-300'
                      : currentTheme === 'grither'
                      ? 'hover:bg-white/10 text-white/80'
                      : 'hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  <User size={20} strokeWidth={2} className="flex-shrink-0" />
                  <span className="font-medium">Пользователи</span>
                </button>
              )}

              <button
                onClick={() => {
                  setActiveTab('profile');
                  setIsMobileMenuOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all mb-1 ${
                  activeTab === 'profile'
                    ? currentTheme === 'dark'
                      ? 'bg-blue-600 text-white'
                      : currentTheme === 'grither'
                      ? 'bg-white/20 text-white'
                      : 'bg-blue-100 text-blue-600'
                    : currentTheme === 'dark'
                    ? 'hover:bg-slate-700 text-slate-300'
                    : currentTheme === 'grither'
                    ? 'hover:bg-white/10 text-white/80'
                    : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                <Settings size={20} strokeWidth={2} className="flex-shrink-0" />
                <span className="font-medium">Профиль</span>
              </button>

              <button
                onClick={() => {
                  setActiveTab('guide');
                  setIsMobileMenuOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all mb-1 ${
                  activeTab === 'guide'
                    ? currentTheme === 'dark'
                      ? 'bg-blue-600 text-white'
                      : currentTheme === 'grither'
                      ? 'bg-white/20 text-white'
                      : 'bg-blue-100 text-blue-600'
                    : currentTheme === 'dark'
                    ? 'hover:bg-slate-700 text-slate-300'
                    : currentTheme === 'grither'
                    ? 'hover:bg-white/10 text-white/80'
                    : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                <BookOpen size={20} strokeWidth={2} className="flex-shrink-0" />
                <span className="font-medium">Руководство</span>
              </button>
            </nav>

            {/* Селектор темы в меню */}
            {isAuthenticated && (
              <div className={`p-4 border-t ${
                currentTheme === 'dark'
                  ? 'border-slate-700'
                  : currentTheme === 'grither'
                  ? 'border-white/20'
                  : 'border-gray-200'
              }`}>
                <label className={`block text-xs font-medium mb-2 ${
                  currentTheme === 'grither' ? 'text-white/70' : getTextClass('secondary')
                }`}>
                  Тема оформления
                </label>
                <select
                  value={currentTheme === 'custom' && activeCustomTheme ? `custom-${activeCustomTheme.id}` : currentTheme}
                  onChange={(e) => {
                    const selectedValue = e.target.value;
                    if (selectedValue.startsWith('custom-')) {
                      const customThemeId = selectedValue.replace('custom-', '');
                      changeTheme('custom', customThemeId);
                    } else {
                      changeTheme(selectedValue);
                    }
                  }}
                  className={`w-full px-3 py-2 rounded-lg text-sm transition-all ${
                    currentTheme === 'dark'
                      ? 'bg-slate-700 text-white border-slate-600'
                      : currentTheme === 'grither'
                      ? 'bg-white/10 text-white border-white/20'
                      : 'bg-gray-100 text-gray-900 border-gray-300'
                  } border`}
                >
                  {Object.entries(themeLabels).map(([themeKey, label]) => {
                    if (themeKey === 'custom') return null;
                    return (
                      <option key={themeKey} value={themeKey}>
                        {themeKey === 'light' && '☀ '}
                        {themeKey === 'dark' && '☽ '}
                        {themeKey === 'grither' && '◇ '} 
                        {' '}{label}
                      </option>
                    );
                  })}
                  {customThemes.length > 0 && (
                    <optgroup label="Кастомные темы">
                      {customThemes.map(theme => (
                        <option key={`custom-${theme.id}`} value={`custom-${theme.id}`}>
                          {theme.is_active && '● '}{theme.display_name}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>
            )}

            {/* Статус бота в меню */}
            <div className={`p-4 border-t ${
              currentTheme === 'dark'
                ? 'border-slate-700'
                : currentTheme === 'grither'
                ? 'border-white/20'
                : 'border-gray-200'
            }`}>
              <div className="flex items-center justify-between">
                <span className={`text-sm ${
                  currentTheme === 'grither' ? 'text-white/70' : getTextClass('secondary')
                }`}>
                  Статус бота
                </span>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <span className={`text-sm font-medium ${
                    currentTheme === 'grither' ? 'text-white' : getTextClass('primary')
                  }`}>
                    Активен
                  </span>
                </div>
              </div>
            </div>

            </div>
            {/* Нижний блок: Выйти + версия — всегда внизу меню */}
            <div className={`flex-shrink-0 border-t ${
              currentTheme === 'dark' ? 'border-slate-700' : currentTheme === 'grither' ? 'border-white/20' : 'border-gray-200'
            }`}>
            {/* Кнопка выхода */}
            <div className="p-4">
              <button
                onClick={logout}
                className="w-full px-4 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all font-medium flex items-center justify-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span>Выйти</span>
              </button>
            </div>

            {/* Версия релиза — внизу бургер-меню (только мобильная) */}
            {releaseVersion != null && (
              <div className={`px-4 pb-4 pt-2 text-center text-xs opacity-70 ${
                currentTheme === 'dark' ? 'text-slate-400' : currentTheme === 'grither' ? 'text-white/60' : 'text-slate-500'
              }`}>
                ver. {releaseVersion}
              </div>
            )}
            </div>
          </div>
        </>
      )}

      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 md:py-8">
        {/* Breadcrumbs */}
        <Breadcrumbs
          items={[
            { label: 'Главная', onClick: () => setActiveTab('overview') },
            { 
              label: activeTab === 'overview' ? 'Обзор' :
                     activeTab === 'clients' ? 'Клиенты' :
                     activeTab === 'deals' ? 'Сделки' :
                     activeTab === 'rates' ? 'Курсы валют' :
                     activeTab === 'users' ? 'Пользователи' :
                     activeTab === 'profile' ? 'Профиль' :
                     activeTab === 'guide' ? 'Руководство' : 'Dashboard'
            }
          ]}
        />

        {/* Tab Navigation - Скрыта на мобильных, отображается на десктопе */}
        <div className="mb-6 md:mb-8 hidden md:block">
          {/* Desktop Navigation - Горизонтальная прокрутка на планшетах */}
          <div className="flex space-x-2 md:space-x-4 overflow-x-auto pb-2 scrollbar-hide">
            <TabButton
              id="overview"
              label="Обзор"
              icon={<BarChart3 />}
              active={activeTab === 'overview'}
              onClick={setActiveTab}
            />
            <TabButton
              id="clients"
              label="Клиенты"
              icon={<Users />}
              active={activeTab === 'clients'}
              onClick={setActiveTab}
            />
            <TabButton
              id="deals"
              label="Сделки"
              icon={<Briefcase />}
              active={activeTab === 'deals'}
              onClick={setActiveTab}
            />
            <TabButton
              id="rates"
              label="Курсы"
              icon={<ArrowLeftRight />}
              active={activeTab === 'rates'}
              onClick={setActiveTab}
            />
            {hasPermission('manage_users') && (
              <TabButton
                id="users"
                label="Юзеры"
                icon={<User />}
                active={activeTab === 'users'}
                onClick={setActiveTab}
              />
            )}
            <TabButton
              id="profile"
              label="Профиль"
              icon={<Settings />}
              active={activeTab === 'profile'}
              onClick={setActiveTab}
            />
            <TabButton
              id="guide"
              label="Инфо"
              icon={<BookOpen />}
              active={activeTab === 'guide'}
              onClick={setActiveTab}
            />
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            {/* Statistics Cards - Enhanced - Адаптивная сетка */}
            {statistics && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-6 md:mb-8">
                <StatCard
                  title="Клиенты"
                  value={statistics.total_clients || 0}
                  subtitle={`Сегодня: +${statistics.today_clients || 0}`}
                  icon={React.createElement(statIcons.clients)}
                  color="blue"
                />
                <StatCard
                  title="Сделки"
                  value={statistics.total_deals || 0}
                  subtitle={`Сегодня: +${statistics.today_deals || 0}`}
                  icon={React.createElement(statIcons.deals)}
                  color="green"
                />
                <StatCard
                  title="Оборот (THB)"
                  value={formatNumber(statistics.total_amount || 0)}
                  subtitle={`Сегодня: ${formatNumber(statistics.today_amount_thb || 0)} ฿`}
                  icon={React.createElement(statIcons.turnover)}
                  color="purple"
                />
                <StatCard
                  title="Средний обмен"
                  value={`${formatNumber(Math.round(statistics.avg_deal_amount_all_time || 0))} ฿`}
                  subtitle={`Сегодня: ${formatNumber(Math.round(statistics.avg_deal_amount_today || 0))} ฿`}
                  icon={React.createElement(statIcons.average)}
                  color="orange"
                />
              </div>
            )}

            {/* Time Period Selector */}
            <TimePeriodSelector />

            {/* Statistics Charts */}
            {statistics && <StatisticsCharts />}

            {/* Today's Rates Section - Compact currency cards */}
            {currencyRates && (
              <div className={`rounded-xl shadow-lg p-4 mb-6 ${currentTheme === 'dark' ? 'modern-dark-card' : getCardClass()}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${currentTheme === 'dark' ? 'bg-emerald-600/50 border border-emerald-500/40' : 'bg-gradient-to-br from-green-500 to-emerald-600'}`}>
                      <ArrowLeftRight size={18} strokeWidth={2} className="text-white" />
                    </div>
                    <div>
                      <h3 className={`text-lg font-bold ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>Курс сегодня</h3>
                      <p className={`text-xs ${currentTheme === 'dark' ? 'text-slate-200' : getTextClass('secondary')}`}>Введённые курсы по сделкам за сегодня</p>
                    </div>
                  </div>
                </div>
                {(() => {
                  const todayRatesEntries = Object.entries(currencyRates).filter(
                    ([currency, data]) =>
                      ['RUB', 'USDT', 'USD', 'EUR'].includes(currency) && data.today_rate != null
                  );
                  if (todayRatesEntries.length === 0) {
                    return (
                      <p className={`text-sm py-3 ${getTextClass('secondary')}`}>
                        Нет курсов за сегодня — появятся после ввода сделок с курсом.
                      </p>
                    );
                  }
                  return (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {todayRatesEntries.map(([currency, data]) => {
                      const currencyInfo = {
                        RUB: { Icon: Coins, name: 'Рубль' },
                        USDT: { Icon: CircleDollarSign, name: 'Tether' },
                        USD: { Icon: DollarSign, name: 'Доллар' },
                        EUR: { Icon: Euro, name: 'Евро' }
                      };
                      const info = currencyInfo[currency] || { Icon: Coins, name: currency };
                      const CurrencyIcon = info.Icon;
                      const todayDeals = data.today_deals ?? 0;

                      return (
                        <div key={currency} className={`currency-card group relative overflow-hidden rounded-lg p-3 transition-all duration-300 hover:shadow-md hover:-translate-y-1 border ${
                          currentTheme === 'dark' ? 'bg-[#1e293b] border-slate-600' :
                          currentTheme === 'grither' ? 'bg-grither-gray-50 border-grither-gray-200' :
                          currentTheme === 'custom' ? 
                            '' :
                          'bg-white border-gray-200 hover:border-gray-300'
                        }`} style={currentTheme === 'custom' ? {
                          backgroundColor: 'var(--theme-currency-card-background, var(--currency-card-background, #ffffff))',
                          borderColor: 'var(--theme-currency-card-border, var(--currency-card-border, #e5e7eb))'
                        } : {}}>
                          
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <CurrencyIcon size={20} strokeWidth={2} className={currentTheme === 'dark' ? 'text-slate-300' : 'text-slate-600'} />
                              <div>
                                <p className={`font-bold text-sm ${getCurrencyCodeClass(currency)}`}>{currency}</p>
                                <p className={`text-xs ${getTextClass('secondary')}`}>{info.name}</p>
                              </div>
                            </div>
                            
                            {/* Right side - today's rate (from deals entered today) */}
                            <div className="text-right">
                              <p className={`text-lg font-bold ${
                                currentTheme === 'dark' ? 'text-blue-400' :
                                currentTheme === 'grither' ? 'text-grither-primary' :
                                currentTheme === 'custom' ? 
                                  '' :
                                'text-gray-800'
                              }`} style={currentTheme === 'custom' ? {
                                color: 'var(--theme-primary-button, var(--primary-button, #1f2937))'
                              } : {}}>
                                {parseFloat(data.today_rate || 0).toFixed(2)}
                              </p>
                              <p className={`text-xs font-medium ${getTextClass('secondary')}`}>
                                {todayDeals > 0 ? `+${todayDeals}` : ''}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
                  );
                })()}
              </div>
            )}

            {/* Recent Activity */}
            <div className="grid md:grid-cols-2 gap-8">
              {/* Recent Deals Section - Correct data structure */}
              {recentDeals.length > 0 && (
                <div className={`rounded-xl shadow-lg p-6 ${currentTheme === 'dark' ? 'modern-dark-card' : getCardClass()}`}>
                  <div className="flex items-center justify-between mb-6">
                    <h3 className={`text-lg font-semibold flex items-center gap-2 ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>
                      <BarChart3 size={20} strokeWidth={2} /> Последние сделки
                    </h3>
                    <span className={`text-sm px-3 py-1 rounded-full ${
                      currentTheme === 'dark' ? 'bg-blue-500/20 text-blue-200 border border-blue-500/30' :
                      currentTheme === 'grither' ? 'bg-grither-gray-100 grither-text-primary' :
                      currentTheme === 'custom' ? 'universal-badge' :
                      'text-blue-600 bg-blue-50'
                    }`}>
                      {recentDeals.length} из последних сделок
                    </span>
                  </div>
                  <div className="space-y-2">
                    {recentDeals.slice(0, 5).map((deal, index) => (
                      <div key={deal.id || index} className={`deal-row py-2 pl-2 pr-3 rounded-lg transition-colors hover:shadow-sm ${currentTheme === 'dark' ? 'modern-dark-row' : getCardClass()}`}>
                        <div className="flex items-center justify-between w-full">
                          {/* СЛЕВА: номер, имя и дата */}
                          <div className="flex items-center space-x-1 justify-start min-w-0">
                            <div className={`deal-number-badge w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                              currentTheme === 'dark' ? 'bg-blue-600 text-white' :
                              currentTheme === 'grither' ? 'bg-grither-primary text-white' :
                              currentTheme === 'custom' ? 'universal-badge' :
                              'bg-blue-500 text-white'
                            }`}>
                              #{index + 1}
                            </div>
                            <div className="min-w-0">
                              <p className={`font-medium truncate text-sm ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>
                                {deal.first_name && deal.last_name ? `${deal.first_name} ${deal.last_name}` : (deal.first_name || 'N/A')}
                              </p>
                              <p className={`text-xs flex items-center gap-1 ${currentTheme === 'dark' ? 'text-slate-200' : getTextClass('secondary')}`}>
                                <Calendar size={14} strokeWidth={2} /> {deal.date}
                              </p>
                            </div>
                          </div>
                          
                          {/* СПРАВА: сумма, валюта, курс */}
                          <div className="text-right flex-shrink-0 ml-2">
                            <p className={`font-bold text-sm ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>
                              {deal.amount ? Math.round(parseFloat(deal.amount)).toLocaleString('ru-RU').replace(/,/g, ' ') : '0'}
                            </p>
                            <p className={`text-xs ${currentTheme === 'dark' ? 'text-slate-200' : getTextClass('secondary')}`}>
                              <span className={getCurrencyCodeClass(deal.currency || '')}>{deal.currency || 'N/A'}</span> • ₽{parseFloat(deal.rate || 0).toFixed(2)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {recentDeals.length > 5 && (
                    <div className="mt-4 text-center">
                      <button 
                        onClick={() => setActiveTab('deals')}
                        className={`${
                          currentTheme === 'dark' ? 'text-blue-300 hover:text-blue-200 text-sm font-medium' :
                          currentTheme === 'custom' ? 'universal-link-button' : 'text-blue-600 hover:text-blue-800 text-sm font-medium'
                        }`}
                      >
                        Показать все сделки →
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Top Clients - Correct data structure */}
              <div className={`rounded-xl shadow-lg p-6 ${currentTheme === 'dark' ? 'modern-dark-card' : getCardClass()}`}>
                <div className="flex justify-between items-center mb-4">
                  <h3 className={`text-lg font-semibold flex items-center gap-2 ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>
                    <Users size={20} strokeWidth={2} /> Активные клиенты
                  </h3>
                  <button
                    onClick={() => setActiveTab('clients')}
                    className={`text-sm px-3 py-1 rounded-full transition-colors hover:scale-105 ${
                      currentTheme === 'dark' ? 'bg-orange-500/20 text-orange-200 border border-orange-500/30 hover:bg-orange-500/30' :
                      currentTheme === 'grither' ? 'bg-orange-100 text-orange-800 hover:bg-orange-200' :
                      currentTheme === 'custom' ? 'universal-badge' :
                      'text-orange-600 bg-orange-50 hover:bg-orange-100'
                    }`}
                  >
                    Все клиенты →
                  </button>
                </div>
                <div className="space-y-2">
                  {clients
                    .sort((a, b) => {
                      // Сначала сортируем по дате создания (новые клиенты вверху)
                      const dateA = new Date(a.created_date || 0);
                      const dateB = new Date(b.created_date || 0);
                      if (dateB.getTime() !== dateA.getTime()) {
                        return dateB.getTime() - dateA.getTime();
                      }
                      // Если даты одинаковые, сортируем по сумме
                      return (b.total_thb_amount || 0) - (a.total_thb_amount || 0);
                    })
                    .slice(0, 5)
                    .map((client, index) => (
                    <div key={index} className={`client-row py-2 px-3 rounded-lg transition-colors hover:shadow-sm ${currentTheme === 'dark' ? 'modern-dark-row' : getCardClass()}`}>
                      <div className="flex items-center justify-between w-full">
                        {/* СЛЕВА: имя и дата последнего контакта */}
                        <div className="flex-1 min-w-0">
                          <p className={`font-medium truncate text-sm ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>{client.name}</p>
                          <p className={`text-xs flex items-center gap-1 ${currentTheme === 'dark' ? 'text-slate-200' : getTextClass('secondary')}`}>
                            <Calendar size={14} strokeWidth={2} /> {client.last_contact && !client.last_contact.includes('Автоматически') ? client.last_contact : 'Недавно'}
                          </p>
                        </div>
                        
                        {/* СПРАВА: количество сделок, общая сумма, обменов */}
                        <div className="text-right flex-shrink-0 ml-3">
                          <p className={`text-sm font-medium ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>
                            {formatNumber(client.total_thb_amount || 0)} THB
                          </p>
                          <p className={`text-xs ${currentTheme === 'dark' ? 'text-slate-200' : getTextClass('secondary')}`}>
                            Обменов {client.total_deals}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}

        {/* Clients Tab */}
        {activeTab === 'clients' && (
          <div className={`rounded-xl shadow-lg p-6 ${currentTheme === 'dark' ? 'modern-dark-card' : 'bg-white'}`}>
            <div className="flex justify-between items-center mb-6">
              <h2 className={`text-xl md:text-2xl font-bold ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>База клиентов</h2>
              <div className="flex space-x-2 md:space-x-3">
                <button
                  onClick={() => setClientsFilters({...clientsFilters, showFilters: !clientsFilters.showFilters})}
                  className={`inline-flex items-center gap-2 px-2 py-1.5 md:px-4 md:py-2 text-xs md:text-sm rounded-lg transition-colors ${currentTheme === 'dark' ? 'bg-slate-600 text-white hover:bg-slate-500 border border-slate-500' : getButtonClass('secondary')}`}
                >
                  <Search size={18} strokeWidth={2} /> <span className="hidden sm:inline">Фильтры</span>
                </button>
                <button
                  onClick={() => exportData('Clients')}
                  className={`inline-flex items-center gap-2 px-2 py-1.5 md:px-4 md:py-2 text-xs md:text-sm rounded-lg transition-colors ${currentTheme === 'dark' ? 'bg-slate-600 text-white hover:bg-slate-500 border border-slate-500' : getButtonClass('secondary')}`}
                  disabled={!hasPermission('export_data')}
                >
                  <Download size={18} strokeWidth={2} /> <span className="hidden sm:inline">Экспорт CSV</span>
                </button>
              </div>
            </div>
            
            {/* Filters Panel */}
            {clientsFilters.showFilters && (
              <div className={`mb-6 p-4 rounded-lg border ${
                currentTheme === 'dark' ? 'bg-gray-800 border-gray-700' :
                currentTheme === 'grither' ? 'bg-grither-gray-50 border-grither-gray-200' :
                currentTheme === 'custom' ? 'universal-card' :
                'bg-gray-50 border-gray-200'
              }`}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${getTextClass('primary')}`}>
                      Поиск по имени
                    </label>
                    <input
                      type="text"
                      value={clientsFilters.name}
                      onChange={(e) => setClientsFilters({...clientsFilters, name: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        currentTheme === 'dark' ? 'modern-dark-input' :
                        currentTheme === 'custom' ? 'universal-input' : 'border-gray-300'
                      }`}
                      placeholder="Введите имя клиента"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${getTextClass('primary')}`}>
                      Поиск по ID
                    </label>
                    <input
                      type="text"
                      value={clientsFilters.id}
                      onChange={(e) => setClientsFilters({...clientsFilters, id: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        currentTheme === 'dark' ? 'modern-dark-input' :
                        currentTheme === 'custom' ? 'universal-input' : 'border-gray-300'
                      }`}
                      placeholder="ID клиента"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${getTextClass('primary')}`}>
                      Сортировка
                    </label>
                    <select
                      value={`${clientsSortBy}_${clientsSortOrder}`}
                      onChange={(e) => {
                        const raw = e.target.value;
                        const parts = raw.split('_');
                        const sortOrder = parts.pop(); // asc или desc
                        const sortBy = parts.join('_'); // total_thb_amount, name, client_id, created_date
                        setClientsSortBy(sortBy);
                        setClientsSortOrder(sortOrder);
                        setTimeout(() => {
                          fetchClients(1, 50, sortBy, sortOrder, {
                            name: clientsFilters.name,
                            id: clientsFilters.id
                          });
                          setClientsPage(1);
                        }, 100);
                      }}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        currentTheme === 'dark' ? 'modern-dark-input' :
                        currentTheme === 'custom' ? 'universal-input' : 'border-gray-300'
                      }`}
                    >
                      <option value="total_thb_amount_desc">По сумме (убыв)</option>
                      <option value="total_thb_amount_asc">По сумме (возр)</option>
                      <option value="name_asc">По имени (А-Я)</option>
                      <option value="name_desc">По имени (Я-А)</option>
                      <option value="client_id_desc">По ID (убыв)</option>
                      <option value="client_id_asc">По ID (возр)</option>
                      <option value="created_date_desc">По дате (новые)</option>
                      <option value="created_date_asc">По дате (старые)</option>
                    </select>
                  </div>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      fetchClients(1, 50, clientsSortBy, clientsSortOrder, {
                        name: clientsFilters.name,
                        id: clientsFilters.id
                      });
                      setClientsPage(1);
                    }}
                    className={`px-4 py-2 rounded-lg transition-colors ${getButtonClass('primary')}`}
                  >
                    Применить фильтры
                  </button>
                  <button
                    onClick={() => {
                      setClientsFilters({...clientsFilters, name: '', id: ''});
                      setClientsSortBy('total_thb_amount');
                      setClientsSortOrder('desc');
                      fetchClients(1, 50, 'total_thb_amount', 'desc', {});
                      setClientsPage(1);
                    }}
                    className={`px-4 py-2 rounded-lg transition-colors ${getButtonClass('secondary')}`}
                  >
                    Сбросить
                  </button>
                </div>
              </div>
            )}
            
            <div className="overflow-x-auto">
              <table className={`w-full table-auto ${getCardClass()}`}>
                <thead>
                  <tr className={`table-header ${
                    currentTheme === 'dark' ? 'bg-gray-800' :
                    currentTheme === 'grither' ? 'bg-grither-gray-50' :
                    currentTheme === 'custom' ? 
                      '' :
                    'bg-gray-50'
                  }`} style={currentTheme === 'custom' ? {
                    backgroundColor: 'var(--theme-table-header-background, var(--table-header-background, #f9fafb))'
                  } : {}}>
                    <th className={`px-4 py-3 text-left text-sm font-medium ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>ID</th>
                    <th className={`px-4 py-3 text-left text-sm font-medium ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>Имя</th>
                    <th className={`px-4 py-3 text-left text-sm font-medium ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>Контакт</th>
                    <th className={`px-4 py-3 text-left text-sm font-medium ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>Источник</th>
                    <th className={`px-4 py-3 text-left text-sm font-medium ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>Сделки</th>
                    <th className={`px-4 py-3 text-left text-sm font-medium ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>Всего обменов THB</th>
                    <th className={`px-4 py-3 text-left text-sm font-medium ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map((client, index) => (
                    <tr 
                      key={index} 
                      onClick={() => setSelectedClientId(client.client_id)}
                      className={`table-row table-row-hover table-row-stripe border-b transition-all cursor-pointer ${
                      currentTheme === 'dark' ? 'border-gray-700 hover:bg-gray-800' :
                      currentTheme === 'grither' ? 'border-grither-gray-200 hover:bg-grither-gray-50' :
                      currentTheme === 'custom' ? 
                        '' :
                      'border-gray-200 hover:bg-gray-50'
                    }`} style={currentTheme === 'custom' ? {
                      borderColor: 'var(--theme-border-color, var(--border-color, #e5e7eb))',
                      backgroundColor: 'var(--theme-table-row-background, var(--table-row-background, #ffffff))'
                    } : {}} onMouseEnter={currentTheme === 'custom' ? (e) => {
                      e.currentTarget.style.backgroundColor = 'var(--theme-secondary-button, var(--secondary-button, #f3f4f6))'
                    } : undefined} onMouseLeave={currentTheme === 'custom' ? (e) => {
                      e.currentTarget.style.backgroundColor = 'var(--theme-table-row-background, var(--table-row-background, #ffffff))'
                    } : undefined}>
                      <td className={`px-4 py-3 text-sm ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>{client.client_id}</td>
                      <td className={`px-4 py-3 text-sm font-medium ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>{client.name}</td>
                      <td className={`px-4 py-3 text-sm ${currentTheme === 'dark' ? 'text-slate-200' : getTextClass('secondary')}`}>
                        {client.last_contact || 'Нет данных'}
                      </td>
                      <td className={`px-4 py-3 text-sm`}>
                        {client.source ? (
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getSourceBadgeClass(client.source)}`}>
                            {client.source}
                          </span>
                        ) : (
                          <span className={currentTheme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>—</span>
                        )}
                      </td>
                      <td className={`px-4 py-3 text-sm font-semibold ${
                        currentTheme === 'dark' ? 'text-blue-400' :
                        currentTheme === 'grither' ? 'text-grither-primary' :
                        currentTheme === 'custom' ? 
                          '' :
                        'text-blue-600'
                      }`} style={currentTheme === 'custom' ? {
                        color: 'var(--theme-primary-button, var(--primary-button, #3b82f6))'
                      } : {}}>{client.total_deals}</td>
                      <td className={`px-4 py-3 text-sm font-semibold ${
                        currentTheme === 'dark' ? 'text-white' :
                        currentTheme === 'grither' ? 'text-green-700' :
                        currentTheme === 'custom' ? 
                          '' :
                        'text-green-600'
                      }`} style={currentTheme === 'custom' ? {
                        color: 'var(--theme-success-color, var(--success-color, #10b981))'
                      } : {}}>
                        {client.total_thb_amount ? Math.round(client.total_thb_amount).toLocaleString('ru-RU').replace(/,/g, ' ') : '0'}
                      </td>
                      <td className="px-4 py-3 text-sm" onClick={(e) => e.stopPropagation()}>
                        <div className="flex space-x-2">
                          {client.phone && (
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(client.phone);
                                alert(`Телефон скопирован: ${client.phone}`);
                              }}
                              className={`px-2 py-1 text-xs rounded transition-all hover:scale-105 ${
                                currentTheme === 'dark' ? 'bg-blue-900/30 text-blue-300 hover:bg-blue-900/50' :
                                currentTheme === 'grither' ? 'bg-grither-primary/10 text-grither-primary hover:bg-grither-primary/20' :
                                'bg-blue-100 text-blue-700 hover:bg-blue-200'
                              }`}
                              title="Копировать телефон"
                            >
                              📞
                            </button>
                          )}
                          {client.telegram_username && (
                            <button
                              onClick={() => {
                                window.open(`https://t.me/${client.telegram_username}`, '_blank');
                              }}
                              className={`px-2 py-1 text-xs rounded transition-all hover:scale-105 ${
                                currentTheme === 'dark' ? 'bg-green-900/30 text-green-300 hover:bg-green-900/50' :
                                currentTheme === 'grither' ? 'bg-green-600/10 text-green-700 hover:bg-green-600/20' :
                                'bg-green-100 text-green-700 hover:bg-green-200'
                              }`}
                              title="Открыть Telegram"
                            >
                              💬
                            </button>
                          )}
                          <button
                            onClick={() => setSelectedClientId(client.client_id)}
                            className={`px-2 py-1 text-xs rounded transition-all hover:scale-105 ${
                              currentTheme === 'dark' ? 'bg-purple-900/30 text-purple-300 hover:bg-purple-900/50' :
                              currentTheme === 'grither' ? 'bg-grither-primary/10 text-grither-primary hover:bg-grither-primary/20' :
                              'bg-purple-100 text-purple-700 hover:bg-purple-200'
                            }`}
                            title="Открыть карточку"
                          >
                            <Eye size={18} strokeWidth={2} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Clients Pagination */}
            <div className="flex flex-col sm:flex-row justify-between items-center mt-6 gap-3">
              <div className={`text-xs sm:text-sm ${currentTheme === 'dark' ? 'text-slate-200' : getTextClass('secondary')}`}>
                Показано {clients.length} записей из 50 на странице {clientsPage}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    if (clientsPage > 1) {
                      const newPage = clientsPage - 1;
                      setClientsPage(newPage);
                      fetchClients(newPage, 50, clientsSortBy, clientsSortOrder, {
                        name: clientsFilters.name,
                        id: clientsFilters.id
                      });
                    }
                  }}
                  disabled={clientsPage <= 1}
                  className={`px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm transition-colors ${
                    clientsPage <= 1 
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : getButtonClass('secondary')
                  }`}
                >
                  <span className="hidden sm:inline">← Пред</span>
                  <span className="sm:hidden">←</span>
                </button>
                <span className={`px-2 sm:px-3 py-1 text-xs sm:text-sm ${getTextClass('primary')}`}>
                  <span className="hidden sm:inline">Страница {clientsPage}</span>
                  <span className="sm:hidden">{clientsPage}</span>
                </span>
                <button
                  onClick={() => {
                    if (clients.length === 50) { // If we got full page, likely more data exists
                      const newPage = clientsPage + 1;
                      setClientsPage(newPage);
                      fetchClients(newPage, 50, clientsSortBy, clientsSortOrder, {
                        name: clientsFilters.name,
                        id: clientsFilters.id
                      });
                    }
                  }}
                  disabled={clients.length < 50}
                  className={`px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm transition-colors ${
                    clients.length < 50
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : getButtonClass('secondary')
                  }`}
                >
                  <span className="hidden sm:inline">След →</span>
                  <span className="sm:hidden">→</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Deals Tab */}
        {activeTab === 'deals' && (
          <div className={`rounded-xl shadow-lg p-6 ${currentTheme === 'dark' ? 'modern-dark-card' : 'bg-white'}`}>
            <div className="flex justify-between items-center mb-6">
              <h2 className={`text-xl md:text-2xl font-bold ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>Журнал сделок</h2>
              <div className="flex space-x-2 md:space-x-3">
                <button
                  onClick={() => setDealsFilters({...dealsFilters, showFilters: !dealsFilters.showFilters})}
                  className={`px-2 py-1.5 md:px-4 md:py-2 text-xs md:text-sm rounded-lg transition-colors ${currentTheme === 'dark' ? 'bg-slate-600 text-white hover:bg-slate-500 border border-slate-500' : getButtonClass('secondary')}`}
                >
                  <Search size={18} strokeWidth={2} /> <span className="hidden sm:inline">Фильтры</span>
                </button>
                <button
                  onClick={() => exportData('Deals')}
                  className={`inline-flex items-center gap-2 px-2 py-1.5 md:px-4 md:py-2 text-xs md:text-sm rounded-lg transition-colors ${currentTheme === 'dark' ? 'bg-slate-600 text-white hover:bg-slate-500 border border-slate-500' : getButtonClass('secondary')}`}
                  disabled={!hasPermission('export_data')}
                >
                  <Download size={18} strokeWidth={2} /> <span className="hidden sm:inline">Экспорт CSV</span>
                </button>
              </div>
            </div>
            
            {/* Deals Filters Panel */}
            {dealsFilters.showFilters && (
              <div className={`mb-6 p-4 rounded-lg border ${
                currentTheme === 'dark' ? 'bg-gray-800 border-gray-700' :
                currentTheme === 'grither' ? 'bg-grither-gray-50 border-grither-gray-200' :
                currentTheme === 'custom' ? 'universal-card' :
                'bg-gray-50 border-gray-200'
              }`}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${getTextClass('primary')}`}>
                      ID клиента
                    </label>
                    <input
                      type="text"
                      value={dealsFilters.client_id}
                      onChange={(e) => setDealsFilters({...dealsFilters, client_id: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        currentTheme === 'dark' ? 'modern-dark-input' :
                        currentTheme === 'custom' ? 'universal-input' : 'border-gray-300'
                      }`}
                      placeholder="ID клиента"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${getTextClass('primary')}`}>
                      Мин. сумма THB
                    </label>
                    <input
                      type="number"
                      value={dealsFilters.amount_min}
                      onChange={(e) => setDealsFilters({...dealsFilters, amount_min: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        currentTheme === 'dark' ? 'modern-dark-input' :
                        currentTheme === 'custom' ? 'universal-input' : 'border-gray-300'
                      }`}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${getTextClass('primary')}`}>
                      Макс. сумма THB
                    </label>
                    <input
                      type="number"
                      value={dealsFilters.amount_max}
                      onChange={(e) => setDealsFilters({...dealsFilters, amount_max: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        currentTheme === 'dark' ? 'modern-dark-input' :
                        currentTheme === 'custom' ? 'universal-input' : 'border-gray-300'
                      }`}
                      placeholder="без ограничений"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${getTextClass('primary')}`}>
                      Дата от
                    </label>
                    <input
                      type="date"
                      value={dealsFilters.date_from}
                      onChange={(e) => setDealsFilters({...dealsFilters, date_from: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        currentTheme === 'dark' ? 'modern-dark-input' :
                        currentTheme === 'custom' ? 'universal-input' : 'border-gray-300'
                      }`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${getTextClass('primary')}`}>
                      Дата до
                    </label>
                    <input
                      type="date"
                      value={dealsFilters.date_to}
                      onChange={(e) => setDealsFilters({...dealsFilters, date_to: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        currentTheme === 'dark' ? 'modern-dark-input' :
                        currentTheme === 'custom' ? 'universal-input' : 'border-gray-300'
                      }`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${getTextClass('primary')}`}>
                      Сортировка
                    </label>
                    <select
                      value={`${dealsSortBy}_${dealsSortOrder}`}
                      onChange={(e) => {
                        const raw = e.target.value;
                        const parts = raw.split('_');
                        const sortOrder = parts.pop(); // asc или desc
                        const sortBy = parts.join('_'); // date, amount_thb, client_id, deal_id
                        setDealsSortBy(sortBy);
                        setDealsSortOrder(sortOrder);
                        setTimeout(() => {
                          fetchRecentDeals(1, 50, sortBy, sortOrder, {
                            client_id: dealsFilters.client_id,
                            amount_min: dealsFilters.amount_min ? parseFloat(dealsFilters.amount_min) : null,
                            amount_max: dealsFilters.amount_max ? parseFloat(dealsFilters.amount_max) : null,
                            date_from: dealsFilters.date_from,
                            date_to: dealsFilters.date_to
                          });
                          setDealsPage(1);
                        }, 100);
                      }}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        currentTheme === 'dark' ? 'modern-dark-input' :
                        currentTheme === 'custom' ? 'universal-input' : 'border-gray-300'
                      }`}
                    >
                      <option value="date_desc">По дате (новые)</option>
                      <option value="date_asc">По дате (старые)</option>
                      <option value="amount_thb_desc">По сумме (убыв)</option>
                      <option value="amount_thb_asc">По сумме (возр)</option>
                      <option value="client_id_desc">По ID клиента (убыв)</option>
                      <option value="client_id_asc">По ID клиента (возр)</option>
                      <option value="deal_id_desc">По ID сделки (убыв)</option>
                      <option value="deal_id_asc">По ID сделки (возр)</option>
                    </select>
                  </div>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      fetchRecentDeals(1, 50, dealsSortBy, dealsSortOrder, {
                        client_id: dealsFilters.client_id,
                        amount_min: dealsFilters.amount_min ? parseFloat(dealsFilters.amount_min) : null,
                        amount_max: dealsFilters.amount_max ? parseFloat(dealsFilters.amount_max) : null,
                        date_from: dealsFilters.date_from,
                        date_to: dealsFilters.date_to
                      });
                      setDealsPage(1);
                    }}
                    className={`px-4 py-2 rounded-lg transition-colors ${getButtonClass('primary')}`}
                  >
                    Применить фильтры
                  </button>
                  <button
                    onClick={() => {
                      setDealsFilters({
                        ...dealsFilters, 
                        client_id: '', 
                        amount_min: '', 
                        amount_max: '', 
                        date_from: '', 
                        date_to: '' 
                      });
                      setDealsSortBy('date');
                      setDealsSortOrder('desc');
                      fetchRecentDeals(1, 50, 'date', 'desc', {});
                      setDealsPage(1);
                    }}
                    className={`px-4 py-2 rounded-lg transition-colors ${getButtonClass('secondary')}`}
                  >
                    Сбросить
                  </button>
                </div>
              </div>
            )}
            
            <div className="overflow-x-auto">
              <table className={`w-full table-auto ${getCardClass()}`}>
                <thead>
                  <tr className={`table-header ${
                    currentTheme === 'dark' ? 'bg-gray-800' :
                    currentTheme === 'grither' ? 'bg-grither-gray-50' :
                    currentTheme === 'custom' ? 
                      '' :
                    'bg-gray-50'
                  }`} style={currentTheme === 'custom' ? {
                    backgroundColor: 'var(--theme-table-header-background, var(--table-header-background, #f9fafb))'
                  } : {}}>
                    <th className={`px-4 py-3 text-left text-sm font-medium ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>Deal ID</th>
                    <th className={`px-4 py-3 text-left text-sm font-medium ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>Имя</th>
                    <th className={`px-4 py-3 text-left text-sm font-medium ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>Сумма</th>
                    <th className={`px-4 py-3 text-left text-sm font-medium ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>Валюта</th>
                    <th className={`px-4 py-3 text-left text-sm font-medium ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>Курс</th>
                    <th className={`px-4 py-3 text-left text-sm font-medium ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>Дата</th>
                    <th className={`px-4 py-3 text-left text-sm font-medium ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>Статус</th>
                    <th className={`px-4 py-3 text-left text-sm font-medium ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>Добавил</th>
                  </tr>
                </thead>
                <tbody>
                  {recentDeals.map((deal, index) => (
                    <tr key={index} className={`table-row border-b transition-colors ${
                      currentTheme === 'dark' ? 'border-gray-700 hover:bg-gray-800' :
                      currentTheme === 'grither' ? 'border-grither-gray-200 hover:bg-grither-gray-50' :
                      currentTheme === 'custom' ? 
                        '' :
                      'border-gray-200 hover:bg-gray-50'
                    }`} style={currentTheme === 'custom' ? {
                      borderColor: 'var(--theme-border-color, var(--border-color, #e5e7eb))',
                      backgroundColor: 'var(--theme-table-row-background, var(--table-row-background, #ffffff))'
                    } : {}} onMouseEnter={currentTheme === 'custom' ? (e) => {
                      e.target.style.backgroundColor = 'var(--theme-secondary-button, var(--secondary-button, #f3f4f6))'
                    } : undefined} onMouseLeave={currentTheme === 'custom' ? (e) => {
                      e.target.style.backgroundColor = 'var(--theme-table-row-background, var(--table-row-background, #ffffff))'
                    } : undefined}>
                      <td className={`px-4 py-3 text-sm ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>#{deal.deal_id}</td>
                      <td className={`px-4 py-3 text-sm ${
                        currentTheme === 'dark' ? 'text-blue-300' :
                        currentTheme === 'grither' ? 'text-grither-primary' :
                        currentTheme === 'custom' ? 
                          '' :
                        'text-blue-600'
                      }`} style={currentTheme === 'custom' ? {
                        color: 'var(--theme-primary-button, var(--primary-button, #3b82f6))'
                      } : {}}>{deal.first_name} {deal.last_name}</td>
                      <td className={`px-4 py-3 text-sm font-medium ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>{deal.amount}</td>
                      <td className={`px-4 py-3 text-sm`}>
                        {deal.currency ? (
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getCurrencyBadgeClass(deal.currency)}`}>
                            {deal.currency}
                          </span>
                        ) : (
                          <span className={currentTheme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>—</span>
                        )}
                      </td>
                      <td className={`px-4 py-3 text-sm ${currentTheme === 'dark' ? 'text-slate-200' : getTextClass('secondary')}`}>{deal.rate}</td>
                      <td className={`px-4 py-3 text-sm ${currentTheme === 'dark' ? 'text-slate-200' : getTextClass('secondary')}`}>{deal.date}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          currentTheme === 'dark' ? 'bg-green-900/30 text-green-300' :
                          currentTheme === 'grither' ? 'bg-grither-gray-100 grither-text-primary' :
                          currentTheme === 'custom' ? 
                            '' :
                          'bg-green-100 text-green-800'
                        }`} style={currentTheme === 'custom' ? {
                          backgroundColor: 'var(--theme-success-color, var(--success-color, #10b981))20',
                          color: 'var(--theme-success-color, var(--success-color, #10b981))'
                        } : {}}>
                          {deal.status}
                        </span>
                      </td>
                      <td className={`px-4 py-3 text-xs ${currentTheme === 'dark' ? 'text-slate-300' : getTextClass('secondary')}`}>
                        {deal.created_by_username ? (
                          <div>
                            <div className="font-medium">{deal.created_by_username}</div>
                            {deal.created_by_role && (
                              <div className={`text-xs px-1 py-0.5 rounded inline-block ${
                                deal.created_by_role === 'ADMIN' 
                                  ? 'bg-purple-100 text-purple-700' 
                                  : deal.created_by_role === 'OPERATOR' 
                                  ? 'bg-blue-100 text-blue-700'
                                  : 'bg-gray-100 text-gray-700'
                              }`}>
                                {deal.created_by_role === 'ADMIN' ? 'Админ' : 
                                 deal.created_by_role === 'OPERATOR' ? 'Оператор' :
                                 deal.created_by_role === 'MANAGER' ? 'Менеджер' :
                                 deal.created_by_role === 'CASHIER' ? 'Кассир' : deal.created_by_role}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className={currentTheme === 'dark' ? 'text-gray-500' : 'text-gray-400'}>—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Deals Pagination */}
            <div className="flex flex-col sm:flex-row justify-between items-center mt-6 gap-3">
              <div className={`text-xs sm:text-sm ${currentTheme === 'dark' ? 'text-slate-200' : getTextClass('secondary')}`}>
                Показано {recentDeals.length} записей из 50 на странице {dealsPage}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    if (dealsPage > 1) {
                      const newPage = dealsPage - 1;
                      setDealsPage(newPage);
                      fetchRecentDeals(newPage, 50, dealsSortBy, dealsSortOrder, {
                        client_id: dealsFilters.client_id,
                        amount_min: dealsFilters.amount_min ? parseFloat(dealsFilters.amount_min) : null,
                        amount_max: dealsFilters.amount_max ? parseFloat(dealsFilters.amount_max) : null,
                        date_from: dealsFilters.date_from,
                        date_to: dealsFilters.date_to
                      });
                    }
                  }}
                  disabled={dealsPage <= 1}
                  className={`px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm transition-colors ${
                    dealsPage <= 1 
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : getButtonClass('secondary')
                  }`}
                >
                  <span className="hidden sm:inline">← Пред</span>
                  <span className="sm:hidden">←</span>
                </button>
                <span className={`px-2 sm:px-3 py-1 text-xs sm:text-sm ${getTextClass('primary')}`}>
                  <span className="hidden sm:inline">Страница {dealsPage}</span>
                  <span className="sm:hidden">{dealsPage}</span>
                </span>
                <button
                  onClick={() => {
                    if (recentDeals.length === 50) { // If we got full page, likely more data exists
                      const newPage = dealsPage + 1;
                      setDealsPage(newPage);
                      fetchRecentDeals(newPage, 50, dealsSortBy, dealsSortOrder, {
                        client_id: dealsFilters.client_id,
                        amount_min: dealsFilters.amount_min ? parseFloat(dealsFilters.amount_min) : null,
                        amount_max: dealsFilters.amount_max ? parseFloat(dealsFilters.amount_max) : null,
                        date_from: dealsFilters.date_from,
                        date_to: dealsFilters.date_to
                      });
                    }
                  }}
                  disabled={recentDeals.length < 50}
                  className={`px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm transition-colors ${
                    recentDeals.length < 50
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : getButtonClass('secondary')
                  }`}
                >
                  <span className="hidden sm:inline">След →</span>
                  <span className="sm:hidden">→</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && hasPermission('manage_users') && (
          <UsersManagement />
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <ProfileSettings />
        )}

        {/* Rates Tab */}
        {activeTab === 'rates' && (
          <div className="space-y-8">
            {/* Currency Rates Overview */}
            <div className={`rounded-xl shadow-lg p-6 ${currentTheme === 'dark' ? 'modern-dark-card' : 'bg-white'}`}>
              <h2 className={`text-2xl font-bold mb-6 flex items-center gap-2 ${getTextClass('primary')}`}>
                <ArrowLeftRight size={28} strokeWidth={2} /> Аналитика курсов валют
              </h2>
              
              {currencyRates && (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {Object.entries(currencyRates)
                    .filter(([currency]) => ['RUB', 'USDT', 'USD', 'EUR'].includes(currency))
                    .map(([currency, data]) => {
                      const cardClasses = getCurrencyCardClasses(currency, currentTheme === 'dark');
                      return (
                    <div key={currency} className={`currency-card rounded-xl p-6 hover-card border ${
                      currentTheme === 'custom' ? '' : `${cardClasses.cardBg} ${cardClasses.cardBorder}`
                    }`} style={currentTheme === 'custom' ? {
                      backgroundColor: 'var(--theme-currency-card-background, var(--currency-card-background, #dbeafe))',
                      borderColor: 'var(--theme-currency-card-border, var(--currency-card-border, #93c5fd))'
                    } : {}}>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className={`text-xl font-bold ${getCurrencyCodeClass(currency)}`}>{currency}</h3>
                        <div className="flex items-center">
                          {currency === 'RUB' && <Coins size={24} strokeWidth={2} className={currentTheme === 'dark' ? 'text-slate-300' : 'text-slate-600'} />}
                          {currency === 'USDT' && <CircleDollarSign size={24} strokeWidth={2} className={currentTheme === 'dark' ? 'text-slate-300' : 'text-slate-600'} />}
                          {currency === 'USD' && <DollarSign size={24} strokeWidth={2} className={currentTheme === 'dark' ? 'text-slate-300' : 'text-slate-600'} />}
                          {currency === 'EUR' && <Euro size={24} strokeWidth={2} className={currentTheme === 'dark' ? 'text-slate-300' : 'text-slate-600'} />}
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <div>
                          <p className={`text-sm ${currentTheme === 'dark' ? 'text-slate-300' : 'text-gray-600'}`}>Средний курс</p>
                          <p className={`text-3xl font-bold ${currentTheme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>{data.avg_rate}</p>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className={`text-xs ${currentTheme === 'dark' ? 'text-slate-400' : 'text-gray-500'}`}>Сделок</p>
                            <p className={`text-lg font-semibold ${currentTheme === 'dark' ? 'text-slate-200' : 'text-gray-800'}`}>{data.deals_count}</p>
                          </div>
                          <div>
                            <p className={`text-xs ${currentTheme === 'dark' ? 'text-slate-400' : 'text-gray-500'}`}>Тренд</p>
                            <p className={`text-lg font-semibold ${
                              currentTheme === 'dark'
                                ? (data.trend > 0 ? 'text-green-400' : data.trend < 0 ? 'text-red-400' : 'text-slate-400')
                                : (data.trend > 0 ? 'text-green-600' : data.trend < 0 ? 'text-red-600' : 'text-gray-600')
                            }`}>
                              {data.trend > 0 ? <><TrendingUp size={18} strokeWidth={2} className="inline-block align-middle mr-0.5" /> </> : data.trend < 0 ? <><TrendingDown size={18} strokeWidth={2} className="inline-block align-middle mr-0.5" /> </> : null}
                              {Math.abs(data.trend).toFixed(2)}%
                            </p>
                          </div>
                        </div>
                        
                        <div>
                          <p className={`text-xs ${currentTheme === 'dark' ? 'text-slate-400' : 'text-gray-500'}`}>Период анализа</p>
                          <p className={`text-sm font-medium ${currentTheme === 'dark' ? 'text-slate-200' : 'text-gray-700'}`}>{data.period}</p>
                        </div>
                        
                        <div className={`pt-2 border-t ${currentTheme === 'dark' ? 'border-slate-600' : 'border-gray-200'}`}>
                          <div className={`flex justify-between text-xs ${currentTheme === 'dark' ? 'text-slate-400' : 'text-gray-500'}`}>
                            <span>Мин: {data.min_rate || 'N/A'}</span>
                            <span>Макс: {data.max_rate || 'N/A'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );})}
                </div>
              )}
              
              {!currencyRates && (
                <div className="text-center py-8">
                  <div className="text-6xl mb-4">📊</div>
                  <h3 className={`text-lg font-semibold mb-2 ${getTextClass('primary')}`}>Данные по курсам загружаются...</h3>
                  <p className={getTextClass('secondary')}>Курсы рассчитываются автоматически на основе сделок</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Guide Tab - Role-based content */}
        {activeTab === 'guide' && (
          <>
            {/* Quick Start Section */}
            <div className={`rounded-xl shadow-lg p-8 mb-8 ${getCardClass()}`}>
              <h2 className={`text-2xl font-bold mb-6 flex items-center gap-2 ${getTextClass('primary')}`}>
                <Rocket size={28} strokeWidth={2} /> Руководство пользователя
              </h2>
              
              {/* Role-specific content */}
              {user.role === 'ADMIN' && (
                <>
                  {/* Admin Guide */}
                  <div className="mb-8">
                    <div className={`p-4 mb-6 rounded-lg border-l-4 ${
                      currentTheme === 'dark' ? 'bg-red-900/20 border-red-500' :
                      currentTheme === 'grither' ? 'bg-grither-gray-50 border-grither-primary' :
                      currentTheme === 'custom' ? 'universal-hint-block border-l-4' :
                      'bg-red-50 border-red-400'
                    }`} style={currentTheme === 'custom' ? {
                      borderLeftColor: 'var(--theme-error-color, var(--error-color, #ef4444))'
                    } : {}}>
                      <div className="flex items-center">
                        <Crown size={28} strokeWidth={2} className="flex-shrink-0 mr-3" />
                        <div>
                          <h3 className={`text-lg font-semibold ${
                            currentTheme === 'dark' ? 'text-red-300' :
                            currentTheme === 'grither' ? 'grither-text-primary' :
                            currentTheme === 'custom' ? '' :
                            'text-red-800'
                          }`} style={currentTheme === 'custom' ? {
                            color: 'var(--theme-error-color, var(--error-color, #ef4444))'
                          } : {}}>Руководство администратора</h3>
                          <p className={`${
                            currentTheme === 'dark' ? 'text-red-400' :
                            currentTheme === 'grither' ? 'grither-text-secondary' :
                            currentTheme === 'custom' ? '' :
                            'text-red-700'
                          }`} style={currentTheme === 'custom' ? {
                            color: 'var(--theme-hint-text, var(--hint-text, #1e293b))'
                          } : {}}>У вас есть полный доступ ко всем функциям системы</p>
                        </div>
                      </div>
                    </div>

                    {/* Admin-specific features */}
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                      <div className={`p-6 rounded-lg ${
                        currentTheme === 'dark' ? 'bg-blue-900/20' :
                        currentTheme === 'grither' ? 'bg-grither-gray-50' :
                        currentTheme === 'custom' ? 'universal-hint-block' :
                        'bg-blue-50'
                      }`}>
                        <h4 className={`font-semibold mb-3 ${
                          currentTheme === 'dark' ? 'text-blue-300' :
                          currentTheme === 'grither' ? 'grither-text-primary' :
                          currentTheme === 'custom' ? '' :
                          'text-blue-900'
                        }`} style={currentTheme === 'custom' ? {
                          color: 'var(--theme-primary-button, var(--primary-button, #3b82f6))'
                        } : {}}><Home size={18} strokeWidth={2} className="inline-block align-middle mr-2" /> Главная панель</h4>
                        <ul className={`text-sm space-y-1 ${
                          currentTheme === 'dark' ? 'text-blue-200' :
                          currentTheme === 'grither' ? 'grither-text-secondary' :
                          currentTheme === 'custom' ? '' :
                          'text-blue-800'
                        }`} style={currentTheme === 'custom' ? {
                          color: 'var(--theme-hint-text, var(--hint-text, #1e293b))'
                        } : {}}>
                          <li>• Просмотр общей статистики</li>
                          <li>• Мониторинг активности бота</li>
                          <li>• Анализ клиентов и сделок</li>
                          <li>• Настройка валютных курсов</li>
                        </ul>
                      </div>
                      
                      <div className={`p-6 rounded-lg ${
                        currentTheme === 'dark' ? 'bg-green-900/20' :
                        currentTheme === 'grither' ? 'bg-grither-gray-50' :
                        currentTheme === 'custom' ? 'universal-hint-block' :
                        'bg-green-50'
                      }`}>
                        <h4 className={`font-semibold mb-3 ${
                          currentTheme === 'dark' ? 'text-green-300' :
                          currentTheme === 'grither' ? 'grither-text-primary' :
                          currentTheme === 'custom' ? '' :
                          'text-green-900'
                        }`} style={currentTheme === 'custom' ? {
                          color: 'var(--theme-success-color, var(--success-color, #10b981))'
                        } : {}}><Users size={18} strokeWidth={2} className="inline-block align-middle mr-2" /> Управление пользователями</h4>
                        <ul className={`text-sm space-y-1 ${
                          currentTheme === 'dark' ? 'text-green-200' :
                          currentTheme === 'grither' ? 'grither-text-secondary' :
                          currentTheme === 'custom' ? '' :
                          'text-green-800'
                        }`} style={currentTheme === 'custom' ? {
                          color: 'var(--theme-hint-text, var(--hint-text, #1e293b))'
                        } : {}}>
                          <li>• Создание новых пользователей</li>
                          <li>• Назначение ролей</li>
                          <li>• Блокировка/разблокировка</li>
                          <li>• Настройка Telegram ID</li>
                        </ul>
                      </div>
                      
                      <div className={`p-6 rounded-lg ${
                        currentTheme === 'dark' ? 'bg-orange-900/20' :
                        currentTheme === 'grither' ? 'bg-grither-gray-50' :
                        currentTheme === 'custom' ? 'universal-hint-block' :
                        'bg-orange-50'
                      }`}>
                        <h4 className={`font-semibold mb-3 ${
                          currentTheme === 'dark' ? 'text-orange-300' :
                          currentTheme === 'grither' ? 'grither-text-primary' :
                          currentTheme === 'custom' ? '' :
                          'text-orange-900'
                        }`} style={currentTheme === 'custom' ? {
                          color: 'var(--theme-primary-button, var(--primary-button, #3b82f6))'
                        } : {}}><BarChart3 size={18} strokeWidth={2} className="inline-block align-middle mr-2" /> Отчеты и аналитика</h4>
                        <ul className={`text-sm space-y-1 ${
                          currentTheme === 'dark' ? 'text-orange-200' :
                          currentTheme === 'grither' ? 'grither-text-secondary' :
                          currentTheme === 'custom' ? '' :
                          'text-orange-800'
                        }`} style={currentTheme === 'custom' ? {
                          color: 'var(--theme-hint-text, var(--hint-text, #1e293b))'
                        } : {}}>
                          <li>• Создание отчетов</li>
                          <li>• Экспорт данных в CSV</li>
                          <li>• Расширенная аналитика</li>
                          <li>• Настройка метрик</li>
                        </ul>
                      </div>
                      
                      <div className={`p-6 rounded-lg ${
                        currentTheme === 'dark' ? 'bg-teal-900/20' :
                        currentTheme === 'grither' ? 'bg-grither-gray-50' :
                        currentTheme === 'custom' ? 'universal-hint-block' :
                        'bg-teal-50'
                      }`}>
                        <h4 className={`font-semibold mb-3 ${
                          currentTheme === 'dark' ? 'text-teal-300' :
                          currentTheme === 'grither' ? 'grither-text-primary' :
                          currentTheme === 'custom' ? '' :
                          'text-teal-900'
                        }`} style={currentTheme === 'custom' ? {
                          color: 'var(--theme-primary-button, var(--primary-button, #3b82f6))'
                        } : {}}><Bot size={18} strokeWidth={2} className="inline-block align-middle mr-2" /> Telegram бот</h4>
                        <ul className={`text-sm space-y-1 ${
                          currentTheme === 'dark' ? 'text-teal-200' :
                          currentTheme === 'grither' ? 'grither-text-secondary' :
                          currentTheme === 'custom' ? '' :
                          'text-teal-800'
                        }`} style={currentTheme === 'custom' ? {
                          color: 'var(--theme-hint-text, var(--hint-text, #1e293b))'
                        } : {}}>
                          <li>• Мониторинг статуса бота</li>
                          <li>• Настройка автоматизации</li>
                          <li>• Управление командами</li>
                          <li>• Интеграция с CRM</li>
                        </ul>
                      </div>
                      
                      <div className={`p-6 rounded-lg ${
                        currentTheme === 'dark' ? 'bg-indigo-900/20' :
                        currentTheme === 'grither' ? 'bg-grither-gray-50' :
                        currentTheme === 'custom' ? 'universal-hint-block' :
                        'bg-indigo-50'
                      }`}>
                        <h4 className={`font-semibold mb-3 ${
                          currentTheme === 'dark' ? 'text-indigo-300' :
                          currentTheme === 'grither' ? 'grither-text-primary' :
                          currentTheme === 'custom' ? '' :
                          'text-indigo-900'
                        }`} style={currentTheme === 'custom' ? {
                          color: 'var(--theme-primary-button, var(--primary-button, #3b82f6))'
                        } : {}}><Settings size={18} strokeWidth={2} className="inline-block align-middle mr-2" /> Системные настройки</h4>
                        <ul className={`text-sm space-y-1 ${
                          currentTheme === 'dark' ? 'text-indigo-200' :
                          currentTheme === 'grither' ? 'grither-text-secondary' :
                          currentTheme === 'custom' ? '' :
                          'text-indigo-800'
                        }`} style={currentTheme === 'custom' ? {
                          color: 'var(--theme-hint-text, var(--hint-text, #1e293b))'
                        } : {}}>
                          <li>• Конфигурация системы</li>
                          <li>• Управление базой данных</li>
                          <li>• Настройка безопасности</li>
                          <li>• Резервное копирование</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {user.role === 'OPERATOR' && (
                <>
                  {/* Operator Guide */}
                  <div className="mb-8">
                    <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-6">
                      <div className="flex items-center">
                        <User size={24} strokeWidth={2} className="flex-shrink-0 mr-3" />
                        <div>
                          <h3 className="text-lg font-semibold text-green-800">Руководство пользователя</h3>
                          <p className="text-green-700">У вас есть доступ к просмотру данных и базовым функциям</p>
                        </div>
                      </div>
                    </div>

                    {/* Operator-specific features */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="bg-blue-50 p-6 rounded-lg">
                        <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2"><Home size={18} strokeWidth={2} /> Главная панель</h4>
                        <ul className="text-sm text-blue-800 space-y-1">
                          <li>• Просмотр основной статистики</li>
                          <li>• Мониторинг активности</li>
                          <li>• Обзор последних сделок</li>
                          <li>• Просмотр валютных курсов</li>
                        </ul>
                      </div>
                      
                      <div className="bg-green-50 p-6 rounded-lg">
                        <h4 className="font-semibold text-green-900 mb-3">👥 Клиенты</h4>
                        <ul className="text-sm text-green-800 space-y-1">
                          <li>• Просмотр списка клиентов</li>
                          <li>• Поиск по базе данных</li>
                          <li>• Просмотр деталей клиента</li>
                          <li>• Фильтрация по критериям</li>
                        </ul>
                      </div>
                      
                      <div className="bg-orange-50 p-6 rounded-lg">
                        <h4 className="font-semibold text-orange-900 mb-3">💼 Сделки</h4>
                        <ul className="text-sm text-orange-800 space-y-1">
                          <li>• Просмотр списка сделок</li>
                          <li>• Анализ статистики</li>
                          <li>• Фильтрация по периоду</li>
                          <li>• Экспорт основных данных</li>
                        </ul>
                      </div>
                      
                      <div className="bg-purple-50 p-6 rounded-lg">
                        <h4 className="font-semibold text-purple-900 mb-3">📈 Статистика</h4>
                        <ul className="text-sm text-purple-800 space-y-1">
                          <li>• Просмотр основных метрик</li>
                          <li>• Анализ трендов</li>
                          <li>• Графики и диаграммы</li>
                          <li>• Базовые отчеты</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Common Telegram Bot Guide for all roles */}
              <div className={`rounded-lg p-6 mb-8 border ${
                currentTheme === 'dark' ? 'bg-gray-800 border-gray-700' :
                currentTheme === 'grither' ? 'bg-grither-gray-50 border-grither-gray-200' :
                currentTheme === 'custom' ? 'universal-card border-gray-200' :
                'bg-white border-gray-200'
              }`}>
                <h3 className={`text-lg font-semibold mb-4 flex items-center ${
                  currentTheme === 'dark' ? 'text-gray-100' :
                  currentTheme === 'grither' ? 'grither-text-primary' :
                  currentTheme === 'custom' ? '' :
                  'text-gray-800'
                }`} style={currentTheme === 'custom' ? {
                  color: 'var(--theme-text-primary, var(--text-primary, #1e293b))'
                } : {}}>
                  <Bot size={28} strokeWidth={2} className="flex-shrink-0 mr-3" />
                  Как работать с Telegram ботом
                </h3>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className={`font-semibold mb-3 flex items-center ${
                      currentTheme === 'dark' ? 'text-gray-200' :
                      currentTheme === 'grither' ? 'grither-text-primary' :
                      currentTheme === 'custom' ? '' :
                      'text-gray-700'
                    }`} style={currentTheme === 'custom' ? {
                      color: 'var(--theme-text-primary, var(--text-primary, #1e293b))'
                    } : {}}>
                      <Smartphone size={20} strokeWidth={2} className="flex-shrink-0 mr-2" />
                      Быстрый старт
                    </h4>
                    <div className="space-y-3">
                      <div className="flex items-start">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0 ${
                          currentTheme === 'dark' ? 'bg-blue-900 text-blue-300' :
                          currentTheme === 'grither' ? 'bg-grither-primary text-white' :
                          currentTheme === 'custom' ? '' :
                          'bg-blue-100 text-blue-600'
                        }`} style={currentTheme === 'custom' ? {
                          backgroundColor: 'var(--theme-primary-button, var(--primary-button, #3b82f6))',
                          color: 'white',
                          opacity: 0.1
                        } : {}}>
                          <span className={`text-sm font-bold ${
                            currentTheme === 'custom' ? 'text-blue-600' : ''
                          }`} style={currentTheme === 'custom' ? {
                            opacity: 1
                          } : {}}>1</span>
                        </div>
                        <p className={`text-sm ${
                          currentTheme === 'dark' ? 'text-gray-300' :
                          currentTheme === 'grither' ? 'grither-text-secondary' :
                          currentTheme === 'custom' ? '' :
                          'text-gray-600'
                        }`} style={currentTheme === 'custom' ? {
                          color: 'var(--theme-text-secondary, var(--text-secondary, #64748b))'
                        } : {}}>Начните чат с ботом @crm_grither_bot</p>
                      </div>
                      <div className="flex items-start">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0 ${
                          currentTheme === 'dark' ? 'bg-blue-900 text-blue-300' :
                          currentTheme === 'grither' ? 'bg-grither-primary text-white' :
                          currentTheme === 'custom' ? '' :
                          'bg-blue-100 text-blue-600'
                        }`} style={currentTheme === 'custom' ? {
                          backgroundColor: 'var(--theme-primary-button, var(--primary-button, #3b82f6))',
                          color: 'white',
                          opacity: 0.1
                        } : {}}>
                          <span className={`text-sm font-bold ${
                            currentTheme === 'custom' ? 'text-blue-600' : ''
                          }`} style={currentTheme === 'custom' ? {
                            opacity: 1
                          } : {}}>2</span>
                        </div>
                        <p className={`text-sm ${
                          currentTheme === 'dark' ? 'text-gray-300' :
                          currentTheme === 'grither' ? 'grither-text-secondary' :
                          currentTheme === 'custom' ? '' :
                          'text-gray-600'
                        }`} style={currentTheme === 'custom' ? {
                          color: 'var(--theme-text-secondary, var(--text-secondary, #64748b))'
                        } : {}}>Отправьте команду /start</p>
                      </div>
                      <div className="flex items-start">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0 ${
                          currentTheme === 'dark' ? 'bg-blue-900 text-blue-300' :
                          currentTheme === 'grither' ? 'bg-grither-primary text-white' :
                          currentTheme === 'custom' ? '' :
                          'bg-blue-100 text-blue-600'
                        }`} style={currentTheme === 'custom' ? {
                          backgroundColor: 'var(--theme-primary-button, var(--primary-button, #3b82f6))',
                          color: 'white',
                          opacity: 0.1
                        } : {}}>
                          <span className={`text-sm font-bold ${
                            currentTheme === 'custom' ? 'text-blue-600' : ''
                          }`} style={currentTheme === 'custom' ? {
                            opacity: 1
                          } : {}}>3</span>
                        </div>
                        <p className={`text-sm ${
                          currentTheme === 'dark' ? 'text-gray-300' :
                          currentTheme === 'grither' ? 'grither-text-secondary' :
                          currentTheme === 'custom' ? '' :
                          'text-gray-600'
                        }`} style={currentTheme === 'custom' ? {
                          color: 'var(--theme-text-secondary, var(--text-secondary, #64748b))'
                        } : {}}>Пересылайте сообщения или отправляйте скриншоты</p>
                      </div>
                      <div className="flex items-start">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0 ${
                          currentTheme === 'dark' ? 'bg-blue-900 text-blue-300' :
                          currentTheme === 'grither' ? 'bg-grither-primary text-white' :
                          currentTheme === 'custom' ? '' :
                          'bg-blue-100 text-blue-600'
                        }`} style={currentTheme === 'custom' ? {
                          backgroundColor: 'var(--theme-primary-button, var(--primary-button, #3b82f6))',
                          color: 'white',
                          opacity: 0.1
                        } : {}}>
                          <span className={`text-sm font-bold ${
                            currentTheme === 'custom' ? 'text-blue-600' : ''
                          }`} style={currentTheme === 'custom' ? {
                            opacity: 1
                          } : {}}>4</span>
                        </div>
                        <p className={`text-sm ${
                          currentTheme === 'dark' ? 'text-gray-300' :
                          currentTheme === 'grither' ? 'grither-text-secondary' :
                          currentTheme === 'custom' ? '' :
                          'text-gray-600'
                        }`} style={currentTheme === 'custom' ? {
                          color: 'var(--theme-text-secondary, var(--text-secondary, #64748b))'
                        } : {}}>Подтвердите данные и сохраните в систему</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className={`font-semibold mb-3 flex items-center ${
                      currentTheme === 'dark' ? 'text-gray-200' :
                      currentTheme === 'grither' ? 'grither-text-primary' :
                      currentTheme === 'custom' ? '' :
                      'text-gray-700'
                    }`} style={currentTheme === 'custom' ? {
                      color: 'var(--theme-text-primary, var(--text-primary, #1e293b))'
                    } : {}}>
                      <Zap size={20} strokeWidth={2} className="flex-shrink-0 mr-2" />
                      Основные функции
                    </h4>
                    <ul className={`text-sm space-y-1.5 ${
                      currentTheme === 'dark' ? 'text-gray-300' :
                      currentTheme === 'grither' ? 'grither-text-secondary' :
                      currentTheme === 'custom' ? '' :
                      'text-gray-600'
                    }`} style={currentTheme === 'custom' ? {
                      color: 'var(--theme-text-secondary, var(--text-secondary, #64748b))'
                    } : {}}>
                      <li>• Автоматическое извлечение данных из сообщений</li>
                      <li>• Распознавание текста на скриншотах (OCR)</li>
                      <li>• Создание клиентов и сделок</li>
                      <li>• Управление курсами валют</li>
                      <li>• SQLite база данных</li>
                      <li>• Создание и экспорт отчетов</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Bot Status */}
              <div className={`rounded-lg p-4 ${
                currentTheme === 'dark' ? 'bg-gray-800' :
                currentTheme === 'grither' ? 'bg-grither-gray-50' :
                currentTheme === 'custom' ? 'universal-hint-block' :
                'bg-gray-50'
              }`}>
                <h4 className={`font-semibold mb-3 flex items-center ${
                  currentTheme === 'dark' ? 'text-gray-100' :
                  currentTheme === 'grither' ? 'grither-text-primary' :
                  currentTheme === 'custom' ? '' :
                  'text-gray-900'
                }`} style={currentTheme === 'custom' ? {
                  color: 'var(--theme-text-primary, var(--text-primary, #1e293b))'
                } : {}}>
                  <Bot size={20} strokeWidth={2} className="flex-shrink-0 mr-2" />
                  Статус бота
                </h4>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="flex justify-between items-center">
                    <span className={`text-sm ${
                      currentTheme === 'dark' ? 'text-gray-400' :
                      currentTheme === 'grither' ? 'grither-text-secondary' :
                      currentTheme === 'custom' ? '' :
                      'text-gray-600'
                    }`} style={currentTheme === 'custom' ? {
                      color: 'var(--theme-text-secondary, var(--text-secondary, #64748b))'
                    } : {}}>Токен:</span>
                    <span className={`text-sm font-mono ${
                      currentTheme === 'dark' ? 'text-gray-200' :
                      currentTheme === 'grither' ? 'grither-text-primary' :
                      currentTheme === 'custom' ? '' :
                      'text-gray-800'
                    }`} style={currentTheme === 'custom' ? {
                      color: 'var(--theme-text-primary, var(--text-primary, #1e293b))'
                    } : {}}>. . . </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className={`text-sm ${
                      currentTheme === 'dark' ? 'text-gray-400' :
                      currentTheme === 'grither' ? 'grither-text-secondary' :
                      currentTheme === 'custom' ? '' :
                      'text-gray-600'
                    }`} style={currentTheme === 'custom' ? {
                      color: 'var(--theme-text-secondary, var(--text-secondary, #64748b))'
                    } : {}}>Статус:</span>
                    <span className={`text-sm font-semibold ${
                      currentTheme === 'dark' ? 'text-green-400' :
                      currentTheme === 'grither' ? 'text-green-600' :
                      currentTheme === 'custom' ? '' :
                      'text-green-600'
                    }`} style={currentTheme === 'custom' ? {
                      color: 'var(--theme-success-color, var(--success-color, #10b981))'
                    } : {}}><Check size={14} strokeWidth={2} className="inline-block align-middle mr-1" /> Работает</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className={`text-sm ${
                      currentTheme === 'dark' ? 'text-gray-400' :
                      currentTheme === 'grither' ? 'grither-text-secondary' :
                      currentTheme === 'custom' ? '' :
                      'text-gray-600'
                    }`} style={currentTheme === 'custom' ? {
                      color: 'var(--theme-text-secondary, var(--text-secondary, #64748b))'
                    } : {}}>База данных:</span>
                    <span className={`text-sm font-semibold ${
                      currentTheme === 'dark' ? 'text-green-400' :
                      currentTheme === 'grither' ? 'text-green-600' :
                      currentTheme === 'custom' ? '' :
                      'text-green-600'
                    }`} style={currentTheme === 'custom' ? {
                      color: 'var(--theme-success-color, var(--success-color, #10b981))'
                    } : {}}><Check size={14} strokeWidth={2} className="inline-block align-middle mr-1" /> Подключено</span>
                  </div>
                </div>
              </div>

              <div className={`rounded-lg p-4 mt-4 border-2 ${
                currentTheme === 'dark' ? 'bg-blue-900/20 border-blue-500/50' :
                currentTheme === 'grither' ? 'bg-grither-primary/10 border-grither-primary/40' :
                currentTheme === 'custom' ? 'universal-hint-block border-[var(--theme-primary-button,#3b82f6)]/40' :
                'bg-blue-50 border-blue-200'
              }`}>
                <p className={`text-sm ${
                  currentTheme === 'dark' ? 'text-blue-200' :
                  currentTheme === 'grither' ? 'grither-text-primary' :
                  currentTheme === 'custom' ? '' :
                  'text-blue-900'
                }`} style={currentTheme === 'custom' ? {
                  color: 'var(--theme-primary-button, var(--primary-button, #1e40af))'
                } : {}}>
                  Сделано для семьи GRITHER. Статистика и учёт всех данных в одном месте.
                </p>
                <p className={`text-sm mt-1 ${
                  currentTheme === 'dark' ? 'text-blue-200' :
                  currentTheme === 'grither' ? 'grither-text-primary' :
                  currentTheme === 'custom' ? '' :
                  'text-blue-900'
                }`} style={currentTheme === 'custom' ? {
                  color: 'var(--theme-primary-button, var(--primary-button, #1e40af))'
                } : {}}>
                  Разработчик @noozhdin
                </p>
              </div>
            </div>

            {user.role === 'ADMIN' && (
              <>
                <div className={`rounded-xl shadow-lg p-4 sm:p-6 md:p-8 ${currentTheme === 'dark' ? 'modern-dark-card' : 'bg-white'}`}>
                  <h2 className={`text-lg sm:text-xl md:text-2xl font-bold mb-3 sm:mb-6 flex items-center gap-2 ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>
                    <Users size={24} strokeWidth={2} /> Логи входов пользователей
                  </h2>
                  <p className={`text-xs sm:text-sm mb-3 sm:mb-4 ${currentTheme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                    Просмотр истории входов пользователей в систему
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setShowLoginLogs(true);
                      fetchLoginLogs();
                    }}
                    className="w-full sm:w-auto px-4 py-2 sm:px-6 sm:py-3 text-sm sm:text-base bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    Просмотреть логи входов
                  </button>
                </div>

                <div className={`rounded-xl shadow-lg p-4 sm:p-6 md:p-8 mt-4 sm:mt-6 ${currentTheme === 'dark' ? 'modern-dark-card' : 'bg-white'}`}>
                <h2 className={`text-lg sm:text-xl md:text-2xl font-bold mb-3 sm:mb-6 flex items-center gap-2 ${currentTheme === 'dark' ? 'text-white' : getTextClass('primary')}`}>
                  <Database size={24} strokeWidth={2} /> Очистка базы данных
                </h2>
                <p className={`text-xs sm:text-sm mb-3 sm:mb-4 ${currentTheme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                  Для тестирования CRM с нуля: очистить только клиентов и сделки (пользователи сохраняются) или полностью очистить БД.
                </p>
                {clearDbSuccess && (
                  <div className={`p-2 sm:p-3 rounded-lg mb-3 sm:mb-4 text-xs sm:text-sm ${currentTheme === 'dark' ? 'bg-green-900/30 text-green-300 border border-green-500/40' : 'bg-green-50 text-green-700'}`}>
                    ✅ {clearDbSuccess}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => { setShowClearDbModal(true); setClearDbError(null); setClearDbSuccess(null); }}
                  className="w-full sm:w-auto px-4 py-2 sm:px-6 sm:py-3 text-sm sm:text-base bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
                >
                  Очистить базу данных
                </button>
              </div>
              </>
            )}
          </>
        )}

      </div>

      {/* Модалка очистки БД (только админ) */}
      {showClearDbModal && user?.role === 'ADMIN' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => !clearDbLoading && setShowClearDbModal(false)}>
          <div className={`rounded-xl shadow-xl p-6 max-w-md w-full mx-4 ${currentTheme === 'dark' ? 'bg-gray-800' : 'bg-white'}`} onClick={e => e.stopPropagation()}>
            <h3 className={`text-lg font-bold mb-4 ${currentTheme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>Очистка базы данных</h3>
            <div className="space-y-4">
              <div>
                <p className={`text-sm mb-2 ${currentTheme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>Режим:</p>
                <label className="flex items-center gap-2 mb-1">
                  <input type="radio" name="clearMode" checked={clearDbMode === 'business_only'} onChange={() => setClearDbMode('business_only')} />
                  <span className={currentTheme === 'dark' ? 'text-gray-200' : 'text-gray-800'}>Только клиенты и сделки (пользователи сохраняются)</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="radio" name="clearMode" checked={clearDbMode === 'full'} onChange={() => setClearDbMode('full')} />
                  <span className={currentTheme === 'dark' ? 'text-gray-200' : 'text-gray-800'}>Полная очистка (все данные, включая пользователей)</span>
                </label>
              </div>
              <div>
                <label className={`block text-sm mb-1 ${currentTheme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>Введите ОЧИСТИТЬ для подтверждения:</label>
                <input
                  type="text"
                  value={clearDbConfirm}
                  onChange={e => setClearDbConfirm(e.target.value)}
                  placeholder="ОЧИСТИТЬ"
                  className={`w-full px-3 py-2 rounded border ${currentTheme === 'dark' ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                />
              </div>
              {clearDbError && <div className="p-2 rounded bg-red-100 text-red-700 text-sm">{clearDbError}</div>}
            </div>
            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={clearDatabaseSubmit}
                disabled={clearDbLoading || clearDbConfirm !== 'ОЧИСТИТЬ'}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {clearDbLoading ? 'Выполняю...' : 'Выполнить очистку'}
              </button>
              <button
                type="button"
                onClick={() => !clearDbLoading && setShowClearDbModal(false)}
                className={`px-4 py-2 rounded-lg ${currentTheme === 'dark' ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно логов входов (только админ) */}
      {showLoginLogs && user?.role === 'ADMIN' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowLoginLogs(false)}>
          <div className={`rounded-xl shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden ${currentTheme === 'dark' ? 'bg-gray-800' : 'bg-white'}`} onClick={e => e.stopPropagation()}>
            <div className={`p-6 border-b ${currentTheme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-xl font-bold ${currentTheme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>История входов пользователей</h3>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-140px)]">
              {loginLogsLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
                  <p className={`mt-4 ${currentTheme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>Загрузка логов...</p>
                </div>
              ) : loginLogs.length === 0 ? (
                <p className={`text-center py-8 ${currentTheme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Логи входов отсутствуют</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className={`border-b ${currentTheme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                        <th className={`px-4 py-3 text-left text-sm font-medium ${currentTheme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Время входа</th>
                        <th className={`px-4 py-3 text-left text-sm font-medium ${currentTheme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Пользователь</th>
                        <th className={`px-4 py-3 text-left text-sm font-medium ${currentTheme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Роль</th>
                        <th className={`px-4 py-3 text-left text-sm font-medium ${currentTheme === 'dark' ? 'text-white' : 'text-gray-900'}`}>IP адрес</th>
                        <th className={`px-4 py-3 text-left text-sm font-medium ${currentTheme === 'dark' ? 'text-white' : 'text-gray-900'}`}>User Agent</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loginLogs.map((log, index) => (
                        <tr key={index} className={`border-b ${currentTheme === 'dark' ? 'border-gray-700 hover:bg-gray-700' : 'border-gray-100 hover:bg-gray-50'}`}>
                          <td className={`px-4 py-3 text-sm ${currentTheme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                            {new Date(log.login_time).toLocaleString('ru-RU')}
                          </td>
                          <td className={`px-4 py-3 text-sm font-medium ${currentTheme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            {log.username}
                          </td>
                          <td className={`px-4 py-3 text-sm`}>
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              log.role === 'ADMIN' 
                                ? 'bg-purple-100 text-purple-700' 
                                : log.role === 'OPERATOR' 
                                ? 'bg-blue-100 text-blue-700'
                                : log.role === 'MANAGER'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-100 text-gray-700'
                            }`}>
                              {log.role === 'ADMIN' ? 'Администратор' : 
                               log.role === 'OPERATOR' ? 'Оператор' :
                               log.role === 'MANAGER' ? 'Менеджер' :
                               log.role === 'CASHIER' ? 'Кассир' : log.role}
                            </span>
                          </td>
                          <td className={`px-4 py-3 text-sm ${currentTheme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                            {log.ip_address || '—'}
                          </td>
                          <td className={`px-4 py-3 text-xs ${currentTheme === 'dark' ? 'text-gray-500' : 'text-gray-500'} max-w-xs truncate`} title={log.user_agent}>
                            {log.user_agent || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            
            <div className={`p-6 border-t flex justify-end ${currentTheme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                type="button"
                onClick={() => setShowLoginLogs(false)}
                className={`px-6 py-2 rounded-lg ${currentTheme === 'dark' ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Client Card Modal */}
      {selectedClientId && (
        <ClientCard
          clientId={selectedClientId}
          onClose={() => setSelectedClientId(null)}
          fetchWithAuth={fetchWithAuth}
          isOpen={!!selectedClientId}
          onDeleted={() => {
            setSelectedClientId(null);
            fetchClients(clientsPage, 50, clientsSortBy, clientsSortOrder, { name: clientsFilters.name, id: clientsFilters.id });
          }}
          canDelete={user?.role === 'ADMIN'}
        />
      )}

      {/* Версия релиза: десктоп — правый нижний угол; мобильная — в бургер-меню внизу */}
      {releaseVersion != null && (
        <div
          className={`fixed bottom-0 right-0 pr-3 pb-1.5 text-xs opacity-70 hidden md:block ${
            currentTheme === 'dark' ? 'text-slate-400' : 'text-slate-500'
          }`}
          style={{ pointerEvents: 'none' }}
        >
          ver. {releaseVersion}
        </div>
      )}
    </div>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <ThemeProvider>
        <AppContent />
      </ThemeProvider>
    </AuthProvider>
  );
};

export default App;